import { ft3dFlagParam } from '../encounter-creation-engine/flag-param.js';
import { replaceHydratedHtml } from '../shared/replace-hydrated-html';
import { hydrateSubtree } from '../shared/hydrate-subtree';
import { AmbiencePlayback, ambienceSceneInteractive } from './ambience-playback.js';
import { resolveRoomAmbience, roomIsRoofed } from '../../../ft3d/engine/ft3d-creation-core.js';
import { windAudioFor } from '../shared/wind-audio-catalog.js';
import { loadAudioCatalog, resolveAudioFile, type AudioCatalog } from '../shared/audio-catalog';
import { ftCampaignAudioSurface } from '../shared/audio-surface';
import { refreshSavedEditorEncounter } from '../shared/editor-encounter-refresh';
import { refuseUnported } from '../janus-engine/unported';
import { commitCreatureSpawn, loadSpawnEncounter, syncSpawnLairs, spawnFailure } from '../shared/spawn-browser-bridge';
import { SIZE_WIDTHS_FT } from '../janus-engine/inventory/lift-occupancy';
import type { Roll as LogWindowRow, RenderLogWindowInput as LogWindowInput } from '../log/types';
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// SIDE-PANELS-INIT.TS — canonical TS port (Wave 2b of plans/bundler-script-load-order-phase2.md). Originally generated from
// includes/js/side-panels-init.js by tools/build/_gen-side-panels-init-ts.mjs. The ONLY changes vs the .js
// are this header + routing 3 cross-file shared symbols (__ftReopenSideInfoWhenSideLogCloses /
// currentItemSearchesIndex / loader) through window (template.js's side already resolves to window). The full
// 75-fn public surface is already window-attached in the body; eval = 2 pure-DOM click listeners.
// CANONICAL SOURCE (2026-06-29): the original side-panels-init.js and its one-off generator
// (_gen-side-panels-init-ts.mjs) are RETIRED to _ai_code_files/retired_js/ts_port_oneoffs/. Edit THIS .ts
// directly — there is no .js to keep in sync and no generator to re-run.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// side-panels-init.js — SIDE PANELS (sideLog + sideInfo + mobile chrome) + accumulated helpers
// ----------------------------------------------------------------------------------------------------
// Extracted verbatim from includes/js/template.js (originally lines ~5889-8051).
// Loaded by includes/template/template_page_footer.php AFTER template.js so BASE_URL,
// ftDebugLog, queryData, getElementValue, getCheckboxValue, getSidelogContext, displayWarning,
// display_warning, updateNavigation, loadPostFunctions, destroyTomSelectIn, validateContext,
// and the pre-section viewport / mobile-chrome helpers (`isNarrowViewport`,
// `syncFtMobilePanelChrome`, `getMobilePanelTab`, `signalMobilePanelTabAttention`,
// `clearMobilePanelTabAttention`, `setMobilePanelTabOpenState`,
// `__ftShouldQuietRefreshSideInfoPanel`, `ftPanelChrome*`) exist as window globals at call time.
//
// HONEST NAMING NOTE: the original SIDE PANELS banner in template.js accumulated a number of
// unrelated helpers over time — audio playback (`playAudio*`, `playAudioID`, `playCampaignAudioID`,
// `playCampaignEncounterAmb`, `matchCampaignAudio`), encounter management (`modifyEncounter`,
// `addEncounterCreaturesToEncounter`,
// `buildTurnWidgetGMControls`, `buildLog`), spell helpers (`checkSpellSlots`), trigger helpers (`clearTriggers`, `clearTriggersFromCoreAbilitiesButton`,
// `toggleTriggerActive`, `ftTriggeredAbilityDebouncedPrimaryClick`), URL helpers (`replaceUrlParam`,
// `openLinkNewTab`), and various search / utility functions (`selectSearchItem`,
// `selectSearchInventoryItem`, `checkIfValidUUID`, `onlyUnique`, `clearSelection`,
// `updateInventoryLineTotal`, `outputSumTotal`). They live here because they were under the
// SIDE PANELS banner in source order; relocating them is a separate cleanup pass.
//
// Pre-section flags promoted to `var` (block-scope `let` → `var`) so the extraction stays in
// sync with the assignment at template.js:~36689 across the script boundary:
//   - `__ftClickTargetInsideSideInfo`
//   - `window.__ftReopenSideInfoWhenSideLogCloses`
// Same for the `window.currentItemSearchesIndex` declaration that remains in template.js (line ~14097)
// — converted to `var` so closeSideInfoPanel() in this file can reset it across files.
//
// Public surface — window exports at the end of this file cover:
//   - Side-info panel controls consumed by trade-trainer-init.js (8 functions)
//   - sideLog open/close controls consumed by lockpick_minigame.js and many PHP onclick handlers
//   - All major public functions called from PHP onclick handlers (openSideForm, openItemInfoValue,
//     showStatusEffects, showSummon, playAudioFile*, modifyEncounter, etc.)
//
// Side effects on load: two module-scope `document.addEventListener('click', ...)` listeners
// (capture + bubble phase) that track whether the click target is inside #sideInfoPanel.
// These run at parse time of this file.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////


/** True during a click event if the click target lives inside #sideInfoPanel (capture phase sets this). */
// `var` (not `let`) so the flag attaches to `window` and stays in sync across the extracted
// side-panels-init.js and template.js (template.js:~36689 still assigns to this flag).
var __ftClickTargetInsideSideInfo = false;
/** After mobile handoff (info → log), close log should reopen info so the user lands back on the item card. */
window.__ftReopenSideInfoWhenSideLogCloses = false;

function ftSidePanelInputValue(id: string): string {
	const el = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
	return el && typeof el.value === 'string' ? el.value : '';
}

function ftSidePanelInputValueOrNull(id: string): string | null {
	const el = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
	return el && typeof el.value === 'string' ? el.value : null;
}

document.addEventListener('click', function (e) {
	const target = e.target as (EventTarget & { closest?(selector: string): Element | null }) | null;
	__ftClickTargetInsideSideInfo = !!(
		target
		&& typeof target.closest === 'function'
		&& target.closest('#sideInfoPanel')
	);
}, true);
document.addEventListener('click', function () {
	queueMicrotask(function () {
		__ftClickTargetInsideSideInfo = false;
	});
}, false);

/** Prefer explicit inline `style.width`, then computed width (`width` transition otherwise leaves offsetWidth stale). */
function ftSidePanelIsOpenFromLayout(panel: HTMLElement | null): boolean {
	if (!panel) return false;
	const raw = panel.style.width;
	let px = typeof raw === 'string' && raw !== '' ? parseFloat(raw) : NaN;
	if (!Number.isNaN(px)) {
		return px > 20;
	}
	const cs =
		typeof window !== 'undefined' && typeof window.getComputedStyle === 'function'
			? window.getComputedStyle(panel)
			: null;
	if (cs) {
		px = parseFloat(cs.width);
		if (!Number.isNaN(px)) return px > 20;
	}
	return panel.offsetWidth > 20;
}

// =============================================================================
// SIDE PANELS (sideLog, sideInfo, mobile chrome)
// Open/close logic, tab activation wrappers, mobile-vs-desktop layout sync,
// partner-context tracking for trade/trainer flows.
// =============================================================================

/**
 * ⛔ FT3D routes never present this drawer (owner, 2026-08-21: "this is totally removed or
 * repressed in FT3D states"). The gate lives HERE, at the single source, rather than on
 * window.openSideLog — the ~40 cross-bundle callers reach the window global, but this file's OWN
 * internal calls resolve to the local binding and would slip past a window-level override.
 *
 * Nothing is unmounted: #sideLog and every element id inside it stay in the DOM for the callers
 * that write to them, and the FT3D HUD presents that content instead (the log, the dice window,
 * the Forms window, and the #div_sidelog_warning toast mirror).
 */
function ft3dHudSuppressesSideLog(): boolean {
	return document.documentElement.classList.contains('ft3d-hud-active');
}

/* Set side log visible and responsive width */
function openSideLog() {
	if (ft3dHudSuppressesSideLog()) return;
	const panel = document.getElementById('sideLog');
	if (!panel) return;
	// User just navigated — clear any press-hold floating label or rogue
	// Bootstrap tooltip popover before showing/refreshing the panel.
	ftClearTransientFloatingUi();
	// On narrow viewports, don't force the panel open — just signal the tab so the
	// user can tap in when they want. Content still loads into the panel's inner DOM.
	if (isNarrowViewport() && !panel.__ftForceOpen) {
		signalMobilePanelTabAttention('log');
		return;
	}
	panel.style.display = 'block';  // Override display: none for mobile
	panel.style.width = '360px';
	setMobilePanelTabOpenState('log', true);
}

/* Set the width of the sidebar to 0 (hide it) */
function closeSideLog() {
	const panel = document.getElementById('sideLog');
	if (!panel) return;
	panel.style.width = '0';
	setMobilePanelTabOpenState('log', false);
	clearMobilePanelTabAttention('log');

	const shouldReturnToInfo = window.__ftReopenSideInfoWhenSideLogCloses;
	window.__ftReopenSideInfoWhenSideLogCloses = false;
	if (
		shouldReturnToInfo
		&& typeof isNarrowViewport === 'function'
		&& isNarrowViewport()
		&& typeof forceOpenSideInfoPanel === 'function'
	) {
		forceOpenSideInfoPanel(true);
	}
}

function isSideLogOpen() {
	const panel = document.getElementById('sideLog');
	return ftSidePanelIsOpenFromLayout(panel);
}

function isSideInfoPanelOpen() {
	const panel = document.getElementById('sideInfoPanel');
	return ftSidePanelIsOpenFromLayout(panel);
}

function ftSidePanelContext() {
	return (typeof window.ftGetEncounterContext === 'function' ? window.ftGetEncounterContext() : null)
		|| (typeof getSidelogContext === 'function' ? getSidelogContext() : null)
		|| {};
}

function ftSidePanelCampaignId() {
	const ctx = ftSidePanelContext();
	return String(ctx.campaign_id || (typeof window.ftGetCampaignId === 'function' ? window.ftGetCampaignId() : '') || '');
}

function ftSidePanelEncounterId() {
	const ctx = ftSidePanelContext();
	return String(ctx.encounter_id || (typeof window.ftGetActiveEncounterId === 'function' ? window.ftGetActiveEncounterId() : '') || '');
}

function ftSidePanelLocationId() {
	const formValue = ftSidePanelInputValue('hidden_input_modify_location_id');
	if (formValue) return formValue;
	const ctx = ftSidePanelContext();
	return String(ctx.location_id || (typeof window.ftGetLocationId === 'function' ? window.ftGetLocationId() : '') || '');
}

function ftSidePanelSetEncounterId(encounterId: string, source: string): string {
	if (typeof window.ftSetActiveEncounterId === 'function') {
		return String(window.ftSetActiveEncounterId(encounterId, source) || '');
	}
	return String(window.ftGetActiveEncounterId?.() || encounterId || '');
}

/**
 * Narrow viewports: tint the Log edge tab with danger while a display_warning()-style
 * block (.bg-warning) is in #div_sidelog_warning and the log drawer is still closed.
 * Cleared when the message is removed or the log panel is opened (user saw the banner).
 */
function syncMobileLogTabSidelogWarningHighlight() {
	// the warning observer can fire before mobile-panel-chrome-bundle.js attaches its globals
	const tab = typeof getMobilePanelTab === 'function' ? getMobilePanelTab('log') : null;
	if (!tab) return;
	if (typeof isNarrowViewport === 'function' && !isNarrowViewport()) {
		tab.classList.remove('is-sidelog-warning');
		return;
	}
	const host = document.getElementById('div_sidelog_warning');
	const hasWarning =
		!!host
		&& !!host.querySelector('.bg-warning')
		&& host.textContent.trim() !== '';
	const drawerClosed = typeof isSideLogOpen !== 'function' || !isSideLogOpen();
	tab.classList.toggle('is-sidelog-warning', !!hasWarning && drawerClosed);
}

/** Toggle roll log — used from XZ expanded mode (controls on the left stay clear of the right dock). */
function toggleSideLog() {
	if (isSideLogOpen()) {
		closeSideLog();
	} else {
		// Explicit user intent → force the panel open even on narrow viewports.
		forceOpenSideLog();
	}
}

/** Force-open the log panel regardless of viewport width (explicit user intent). */
function forceOpenSideLog() {
	if (ft3dHudSuppressesSideLog()) return;
	const panel = document.getElementById('sideLog');
	if (!panel) return;
	const handoffFromInfo = (
		typeof isNarrowViewport === 'function'
		&& isNarrowViewport()
		&& __ftClickTargetInsideSideInfo
		&& typeof isSideInfoPanelOpen === 'function'
		&& isSideInfoPanelOpen()
	);
	panel.__ftForceOpen = true;
	try {
		openSideLog();
	} finally {
		panel.__ftForceOpen = false;
	}
	if (handoffFromInfo && typeof collapseSideInfoPanelKeepContent === 'function') {
		collapseSideInfoPanelKeepContent();
		window.__ftReopenSideInfoWhenSideLogCloses = true;
	}
}

/**
 * Complete Bootstrap 5 Tooltip hide synchronously so queued transition callbacks
 * cannot run after dispose(). dispose() uses BaseComponent.dispose(), which nulls
 * instance fields while hide() may still run executeAfterTransition → complete()
 * calls _isWithActiveTrigger() on a null _activeTrigger (Tooltip internal bug/race).
 */
function ftBootstrapTooltipHideSync(inst: BootstrapTooltipInstance | null | undefined): void {
	if (!inst || typeof inst._isShown !== 'function' || !inst._isShown()) return;
	try {
		if (inst._config && typeof inst._config === 'object') {
			inst._config.animation = false;
		}
		if (inst.tip && inst.tip.classList) {
			inst.tip.classList.remove('fade');
		}
		inst.hide();
	} catch (e) {
		/* ignore */
	}
}

function ftDisposeBootstrapTooltipInstanceSafe(inst: BootstrapTooltipInstance | null | undefined): void {
	if (!inst || typeof bootstrap === 'undefined' || !bootstrap.Tooltip) return;
	try {
		// Finish an earlier show/hide fade while Bootstrap's instance is still alive.
		inst.tip?.dispatchEvent(new Event('transitionend'));
		ftBootstrapTooltipHideSync(inst);
		inst.dispose();
	} catch (e) {
		/* ignore */
	}
}

/**
 * Clear any transient floating UI (press-hold label, visible Bootstrap tooltips,
 * orphan .tooltip popovers) that the user shouldn't see hanging over a freshly
 * opened side panel. Called from openSideLog / openSideInfoPanel so that any
 * label/tooltip raised by a tap-and-hold on the icon that triggered navigation
 * is cleaned up immediately — particularly important on iOS / Chrome touch
 * emulation where pointerup / mouseout ordering can leave debris on screen.
 */
function ftClearTransientFloatingUi() {
	if (typeof _ftPressHoldHideLabel === 'function') {
		try { _ftPressHoldHideLabel(); } catch (e) { /* ignore */ }
	}
	if (typeof bootstrap !== 'undefined' && bootstrap.Tooltip) {
		const tooltip = bootstrap.Tooltip;
		document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
			try {
				const inst = tooltip.getInstance(el);
				if (inst) ftBootstrapTooltipHideSync(inst);
			} catch (e) { /* ignore */ }
		});
	}
	document.querySelectorAll('.tooltip').forEach((tip) => {
		const id = tip.getAttribute('id');
		if (!id || !document.querySelector(`[aria-describedby="${id}"]`)) {
			tip.remove();
		}
	});
}

function ftDisposeBootstrapTooltips(elements: Iterable<Element>): void {
	if (typeof bootstrap === 'undefined' || !bootstrap.Tooltip) return;
	const tooltip = bootstrap.Tooltip;
	for (const el of elements) {
		const tipIds = (el.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
		try {
			const inst = tooltip.getInstance(el);
			if (inst) ftDisposeBootstrapTooltipInstanceSafe(inst);
		} catch (e) { /* ignore */ }
		// Tooltip portals live outside the subtree. Retire only this control's portals;
		// opening a panel owns the separate document-wide transient-UI cleanup.
		tipIds.forEach(id => {
			const tip = document.getElementById(id);
			if (tip?.classList.contains('tooltip')) tip.remove();
		});
	}
}

/**
 * Tear down descendant tooltips before replacing innerHTML; the retained root keeps its own tooltip.
 */
function disposeBootstrapTooltipsWithin(rootEl: Element | null | undefined): void {
	if (rootEl) ftDisposeBootstrapTooltips(rootEl.querySelectorAll('[data-bs-toggle="tooltip"]'));
}

/**
 * Dispose a single Bootstrap 5 tooltip and its owned portals (e.g. icon_map wrapper span).
 */
function _ftDisposeBootstrapTooltipOnElement(el: Element | null | undefined): void {
	if (el) ftDisposeBootstrapTooltips([el]);
}

/**
 * If iconEl is .ft-icon-map, returns an interactive ancestor (same selector list as press-hold deferral).
 */
function _ftFtIconMapClickableAncestorWrapper(iconEl: Element | null | undefined): Element | null {
	if (!iconEl || !iconEl.parentElement || !iconEl.classList || !iconEl.classList.contains('ft-icon-map')) {
		return null;
	}
	return iconEl.parentElement.closest('a, button, [onclick], [role="button"]');
}

function _ftTearDownSideinfoHost(el: HTMLElement | null): void {
	if (!el) return;
	destroyTomSelectIn(el);
	disposeBootstrapTooltipsWithin(el);
	if (typeof Chart !== 'undefined' && typeof Chart.getChart === 'function') {
		const getChart = Chart.getChart;
		el.querySelectorAll('canvas').forEach((canvas) => {
			try {
				const ch = getChart(canvas);
				if (ch) ch.destroy();
			} catch (e) { /* ignore */ }
		});
	}
	if (typeof Quill !== 'undefined' && typeof Quill.find === 'function') {
		el.querySelectorAll('.quill-editor').forEach((editor) => {
			try {
				const q = Quill.find(editor);
				if (q) q.disable();
			} catch (e) { /* ignore */ }
		});
	}
	el.innerHTML = '';
}

/**
 * Tear down rich widgets inside the side info area, then clear HTML.
 * Call before emptying so TomSelect / tooltips / charts do not linger and fire after close.
 */
function clearSideInfoPanelContent() {
	const side = document.getElementById('div_refresh_side_info');
	if (side) {
		_ftTearDownSideinfoHost(side);
	}
	_ftTearDownSideinfoHost(document.getElementById('div_sideinfo_merchant_panel'));
	_ftTearDownSideinfoHost(document.getElementById('div_sideinfo_trainer_panel'));
	const ex = document.getElementById('div_sideinfo_exchange_panel');
	if (ex) {
		_ftTearDownSideinfoHost(ex);
		const mtLoad = document.getElementById('ft_monster_template_exchange_loading');
		if (mtLoad) {
			mtLoad.classList.add('d-none');
			mtLoad.setAttribute('aria-busy', 'false');
		}
	}
	window.__sideinfoPartnerContext = null;
	sideInfoHideExchangeTabUi();
	sideInfoActivateItemTab();
}

/** Empty monster-template compiled preview host (#div_sideinfo_exchange_panel) only. */
function clearSideInfoExchangePanelContent() {
	const ex = document.getElementById('div_sideinfo_exchange_panel');
	if (!ex) return;
	_ftTearDownSideinfoHost(ex);
	const mtLoad = document.getElementById('ft_monster_template_exchange_loading');
	if (mtLoad) {
		mtLoad.classList.add('d-none');
		mtLoad.setAttribute('aria-busy', 'false');
	}
}

function clearSideInfoMerchantPanelContent() {
	const el = document.getElementById('div_sideinfo_merchant_panel');
	if (el) _ftTearDownSideinfoHost(el);
}

function clearSideInfoTrainerPanelContent() {
	const el = document.getElementById('div_sideinfo_trainer_panel');
	if (el) _ftTearDownSideinfoHost(el);
}

function sideInfoHideExchangeTabUi() {
	const wrap = document.getElementById('sideinfo_tab_group_wrap');
	if (wrap) wrap.classList.add('d-none');
	const mountM = document.getElementById('sideinfo_merchant_tab_icon_mount');
	const mountT = document.getElementById('sideinfo_trainer_tab_icon_mount');
	if (mountM) mountM.replaceChildren();
	if (mountT) mountT.replaceChildren();
	const btnM = document.getElementById('sideinfo_tab_btn_merchant');
	const btnT = document.getElementById('sideinfo_tab_btn_trainer');
	if (btnM) {
		btnM.classList.add('d-none');
		btnM.classList.remove('d-inline-flex', 'active');
		btnM.setAttribute('aria-pressed', 'false');
		btnM.setAttribute('title', 'Trade');
	}
	if (btnT) {
		btnT.classList.add('d-none');
		btnT.classList.remove('d-inline-flex', 'active');
		btnT.setAttribute('aria-pressed', 'false');
		btnT.setAttribute('title', 'Training');
	}
}

/**
 * Show merchant and/or trainer tab buttons (encounter trade/train or monster-template preview on merchant).
 * @param {{ showMerchant?: boolean, showTrainer?: boolean, merchantTabKind?: 'trade'|'monster_template_preview' }} opts
 */
function sideInfoShowPartnerTabs(opts: {
	showMerchant?: boolean;
	showTrainer?: boolean;
	merchantTabKind?: 'trade' | 'monster_template_preview';
}): void {
	const showMerchant = !!opts.showMerchant;
	const showTrainer = !!opts.showTrainer;
	const merchantTabKind = opts.merchantTabKind || 'trade';
	const wrap = document.getElementById('sideinfo_tab_group_wrap');
	if (wrap) wrap.classList.remove('d-none');
	const btnM = document.getElementById('sideinfo_tab_btn_merchant');
	const btnT = document.getElementById('sideinfo_tab_btn_trainer');
	const mountM = document.getElementById('sideinfo_merchant_tab_icon_mount');
	const mountT = document.getElementById('sideinfo_trainer_tab_icon_mount');
	if (showMerchant && btnM) {
		btnM.classList.remove('d-none');
		btnM.classList.add('d-inline-flex');
		const isMt = merchantTabKind === 'monster_template_preview';
		const tplId = isMt ? 'sideinfo_tpl_exchange_monster_template_preview' : 'sideinfo_tpl_exchange_merchant';
		const title = isMt ? 'Compiled preview' : 'Trade';
		if (mountM) {
			mountM.replaceChildren();
			const tpl = document.getElementById(tplId) as HTMLTemplateElement | null;
			if (tpl && tpl.content) mountM.appendChild(tpl.content.cloneNode(true));
		}
		btnM.setAttribute('title', title);
	} else if (btnM) {
		btnM.classList.add('d-none');
		btnM.classList.remove('d-inline-flex', 'active');
		if (mountM) mountM.replaceChildren();
	}
	if (showTrainer && btnT) {
		btnT.classList.remove('d-none');
		btnT.classList.add('d-inline-flex');
		if (mountT) {
			mountT.replaceChildren();
			const tpl = document.getElementById('sideinfo_tpl_exchange_trainer') as HTMLTemplateElement | null;
			if (tpl && tpl.content) mountT.appendChild(tpl.content.cloneNode(true));
		}
		btnT.setAttribute('title', 'Training');
	} else if (btnT) {
		btnT.classList.add('d-none');
		btnT.classList.remove('d-inline-flex', 'active');
		if (mountT) mountT.replaceChildren();
	}
}

/**
 * @param {'trade'|'train'|'monster_template_preview'} context
 */
function sideInfoShowExchangeTabUi(context: 'trade' | 'train' | 'monster_template_preview'): void {
	const c = window.__sideinfoPartnerContext;
	if (context === 'monster_template_preview') {
		sideInfoShowPartnerTabs({
			showMerchant: true,
			showTrainer: false,
			merchantTabKind: 'monster_template_preview',
		});
		return;
	}
	if (context === 'trade') {
		sideInfoShowPartnerTabs({
			showMerchant: true,
			showTrainer: !!(c && c.hasTrainer),
			merchantTabKind: 'trade',
		});
		return;
	}
	if (context === 'train') {
		sideInfoShowPartnerTabs({
			showMerchant: !!(c && c.hasMerchant),
			showTrainer: true,
			merchantTabKind: 'trade',
		});
	}
}

function sideInfoSetActiveSidePane(which: 'item' | 'merchant' | 'trainer'): void {
	const paneItem = document.getElementById('sideinfo_tab_pane_item');
	const paneM = document.getElementById('sideinfo_tab_pane_merchant');
	const paneT = document.getElementById('sideinfo_tab_pane_trainer');
	if (ft3dHudSuppressesSideInfo()) {
		const host = document.getElementById('div_refresh_side_info');
		const mount = host?.closest('[data-ft3d-side-info-mount]');
		if (mount) {
			// The initial Workspace portal moves the item host only. Keep its partner panes
			// and controls with it so trade/training cannot render in the collapsed drawer.
			if (paneItem && host && host.parentElement !== paneItem) paneItem.appendChild(host);
			for (const node of [document.getElementById('sideinfo_tab_group_wrap'), paneItem, paneM, paneT]) {
				if (node && node.parentElement !== mount) mount.appendChild(node);
			}
		}
	}
	const btnItem = document.getElementById('sideinfo_tab_btn_item');
	const btnM = document.getElementById('sideinfo_tab_btn_merchant');
	const btnT = document.getElementById('sideinfo_tab_btn_trainer');
	if (paneItem) paneItem.classList.toggle('d-none', which !== 'item');
	if (paneM) paneM.classList.toggle('d-none', which !== 'merchant');
	if (paneT) paneT.classList.toggle('d-none', which !== 'trainer');
	if (btnItem) {
		btnItem.classList.toggle('active', which === 'item');
		btnItem.setAttribute('aria-pressed', which === 'item' ? 'true' : 'false');
	}
	if (btnM && !btnM.classList.contains('d-none')) {
		btnM.classList.toggle('active', which === 'merchant');
		btnM.setAttribute('aria-pressed', which === 'merchant' ? 'true' : 'false');
	} else if (btnM) {
		btnM.classList.remove('active');
		btnM.setAttribute('aria-pressed', 'false');
	}
	if (btnT && !btnT.classList.contains('d-none')) {
		btnT.classList.toggle('active', which === 'trainer');
		btnT.setAttribute('aria-pressed', which === 'trainer' ? 'true' : 'false');
	} else if (btnT) {
		btnT.classList.remove('active');
		btnT.setAttribute('aria-pressed', 'false');
	}
}

// Thin HTML-callable wrappers around sideInfoSetActiveSidePane.
// Kept as named functions because PHP/HTML onclick handlers reference them by name.
function sideInfoActivateItemTab()     { sideInfoSetActiveSidePane('item'); }
function sideInfoActivateMerchantTab() { sideInfoSetActiveSidePane('merchant'); }
function sideInfoActivateTrainerTab()  { sideInfoSetActiveSidePane('trainer'); }
/** @deprecated Prefer sideInfoActivateMerchantTab — kept for monster template tool call sites. */
function sideInfoActivateExchangeTab() { sideInfoActivateMerchantTab(); }

function sideInfoOnMerchantTabClick() {
	if (window.FT_MONSTER_TEMPLATE_CREATE_PAGE) {
		sideInfoActivateMerchantTab();
		return;
	}
	const c = window.__sideinfoPartnerContext;
	if (!c || !c.partnerEcId || !c.playerEcId) return;
	if (window.trainState && window.trainState.active) {
		exitTrainMode({ restoreTrainer: false });
		enterTradeMode(c.partnerEcId, c.playerEcId, c.hasTrainer);
		return;
	}
	if (
		(!window.tradeState || !window.tradeState.active)
		&& !document.getElementById('div_merchant_trade_panel')
	) {
		enterTradeMode(c.partnerEcId, c.playerEcId, c.hasTrainer);
		return;
	}
	sideInfoActivateMerchantTab();
}

function sideInfoOnTrainerTabClick() {
	const c = window.__sideinfoPartnerContext;
	if (!c || !c.partnerEcId || !c.playerEcId) return;
	if (window.tradeState && window.tradeState.active) {
		exitTradeMode({ restoreMerchant: false });
		enterTrainMode(c.partnerEcId, c.playerEcId, c.hasMerchant);
		return;
	}
	if (
		(!window.trainState || !window.trainState.active)
		&& !document.getElementById('div_trainer_train_panel')
	) {
		enterTrainMode(c.partnerEcId, c.playerEcId, c.hasMerchant);
		return;
	}
	sideInfoActivateTrainerTab();
}

/**
 * ⛔ Same story as the log drawer: on an FT3D route the Info drawer is never presented (owner,
 * 2026-08-21 — the Info tab is being phased out for a window system). The canonical render host
 * #div_refresh_side_info and its active partner panes live in the Workspace window, so openItemInfo()
 * and clickable log references still work. Raise it here so a
 * caller that expected a drawer to appear still gets something on screen.
 *
 * Gated at the source for the same reason as openSideLog(): this file's own internal calls resolve
 * to the local binding and would walk past a window-level override.
 */
function ft3dHudSuppressesSideInfo(): boolean {
    return document.documentElement.classList.contains('ft3d-hud-active')
        && !!document.querySelector('[data-ft3d-side-info-mount] #div_refresh_side_info');
}

function ft3dRaiseSideInfoWindow(): void {
    const open = (window as unknown as { ftOpenFt3dSideInfo?: () => void }).ftOpenFt3dSideInfo;
    if (typeof open === 'function') open();
}

function openSideInfoPanel() {
    if (ft3dHudSuppressesSideInfo()) {
        ft3dRaiseSideInfoWindow();
        return;
    }
	const panel = document.getElementById('sideInfoPanel');
	if (!panel) return;
	// User just navigated — clear any press-hold floating label or rogue
	// Bootstrap tooltip popover before showing/refreshing the panel.
	ftClearTransientFloatingUi();
	// On narrow viewports, don't force the panel open — signal the Info tab so the
	// user can tap in when they want. Content still loads into #div_refresh_side_info.
	if (isNarrowViewport() && !panel.__ftForceOpen) {
		// Drawer already open (e.g. after forceOpenSideInfoPanel) — keep tab state in sync;
		// do not re-pulse attention (avoids yellow tab while the panel is visible).
		if (ftSidePanelIsOpenFromLayout(panel)) {
			setMobilePanelTabOpenState('info', true);
			return;
		}
		signalMobilePanelTabAttention('info');
		return;
	}
	panel.style.display = 'block';  // Override display: none for mobile
	panel.style.width = '360px';
	setMobilePanelTabOpenState('info', true);
}

/**
 * Narrow-viewport handoff: hide #sideInfoPanel without clearing DOM (user opened #sideLog from inside info).
 */
function collapseSideInfoPanelKeepContent() {
	const panel = document.getElementById('sideInfoPanel');
	if (panel) {
		panel.style.width = '0';
	}
	setMobilePanelTabOpenState('info', false);
	clearMobilePanelTabAttention('info');
}

/* Set the width of the sidebar to 0 (hide it) */
function closeSideInfoPanel() {
	window.__ftReopenSideInfoWhenSideLogCloses = false;
	const panel = document.getElementById('sideInfoPanel');
	if (panel) {
		panel.style.width = '0';
	}
	setMobilePanelTabOpenState('info', false);
	clearMobilePanelTabAttention('info');

	clearSideInfoPanelContent();
	// No "active" side item — avoids refreshSidePanel / inventory refresh refetching after close
	window.currentItemSearchesIndex = -1;
	try {
		sessionStorage.setItem('currentItemSearchesIndex', '-1');
	} catch (e) { /* ignore */ }
	if (typeof updateNavigation === 'function') {
		updateNavigation();
	}
}

/**
 * Force-open the info panel regardless of viewport width (used by mobile-tab tap + drag dwell).
 *
 * `allowOverSidelogForm`: explicit user-gesture override. When false (default),
 * narrow viewport + active sidelog form pane = refuse to open. This stops
 * background events (modifyInventory post-save refresh, openItemInfoValue blur,
 * inventory drag refresh, ability roll refresh) from covering the form the user
 * is editing on phone / iPad. Only the Info tab tap, edge-swipe-open, drag dwell,
 * and the "return to info" handoff after closing the log pass true here.
 */
function forceOpenSideInfoPanel(allowOverSidelogForm = false) {
	if (ft3dHudSuppressesSideInfo()) {
		ft3dRaiseSideInfoWindow();
		return;
	}
	const panel = document.getElementById('sideInfoPanel');
	if (!panel) return;
	if (
		!allowOverSidelogForm
		&& typeof isNarrowViewport === 'function'
		&& isNarrowViewport()
		&& typeof isSideLogFormPaneActive === 'function'
		&& isSideLogFormPaneActive()
	) {
		return;
	}
	panel.__ftForceOpen = true;
	try {
		openSideInfoPanel();
	} finally {
		panel.__ftForceOpen = false;
	}
}

/**
 * Open the side info panel to a large, hover-to-identify view of a rune.
 *
 * Called from the rune medallion click handler in window.ftMountRuneIcons
 * (template_page_footer.php), which passes the already-compiled `ir`. Renders entirely
 * client-side via RuneIcon.renderDetail (rune-icon-bundle.js) — no AJAX round-trip — into
 * the item tab pane host (#div_refresh_side_info). The .ft-rune-detail wrapper scopes the
 * shared .rf-* preview styles (css/style.css) so the dark rune tile reads correctly inside
 * the themed panel. Minimal view (no full legend): big rune + name + type + hover readout.
 * Cleared naturally by clearSideInfoPanelContent() on close (plain DOM + listeners drop on
 * innerHTML replace).
 */
function openRuneInfo(ir: unknown): void {
	if (!ir) return;
	forceOpenSideInfoPanel(true); // explicit user gesture — open even over a sidelog form
	sideInfoActivateItemTab();
	const host = document.getElementById('div_refresh_side_info');
	if (!host || !window.RuneIcon || typeof window.RuneIcon.renderDetail !== 'function') {
		return;
	}
	host.innerHTML = '';
	// The rune preview is a transient overlay drawn on top of whatever item is the
	// current search-history entry (the ability/spell the user opened). It does NOT
	// push its own history entry, so flag the panel as "showing a rune overlay": the
	// side-info Back arrow (goPreviousItemSearches) checks this so the FIRST Back pops
	// the overlay and restores the underlying ability instead of skipping past it to
	// the previous item. Cleared by openItemInfo() on any real item render.
	window.__ftSideInfoRuneOverlayActive = true;
	// Stash the compiled rune so Forward can re-open this exact symbol after a Back,
	// and forget any pending-restore now that the overlay is live (not pending).
	window.__ftSideInfoRuneOverlayIr = ir;
	window.__ftSideInfoRunePendingForwardIndex = null;
	if (typeof updateNavigation === 'function') updateNavigation(); // enable Back even at history index 0
	const wrap = document.createElement('div');
	wrap.className = 'ft-rune-detail';
	const pv = document.createElement('div');
	pv.className = 'rf-preview';
	wrap.appendChild(pv);
	host.appendChild(wrap);
	try {
		window.RuneIcon.renderDetail(pv, ir, { legend: false, sizes: false, meta: false, typeLabel: true });
	} catch (e) {
		console.warn('openRuneInfo: renderDetail failed', e);
	}
}
Object.assign(window, { openRuneInfo });


// Read/write a value cell that may be an <input> OR static text.
//
// The character creator's inventory table used to render unit cost and line total as DISABLED,
// unnamed <input>s — never posted, purely display, and wide enough to force the table into a
// horizontal scrollbar. They are <span>s as of 2026-08-19 (tr_character_inventory_inc.php and its
// TS twin ftRenderCreatorInventoryRows). A span has no `.value`, so the unguarded `el.value` reads
// below returned undefined -> NaN and the grand total would have shown 0. These helpers keep BOTH
// shapes working, because the same two functions are shared with panes that still use real inputs.
function ftCellValue(el: Element | null): string {
	if (!el) return '';
	const asInput = el as HTMLInputElement;
	return typeof asInput.value === 'string' ? asInput.value : (el.textContent ?? '');
}
function ftSetCellValue(el: Element | null, value: string): void {
	if (!el) return;
	const asInput = el as HTMLInputElement;
	if (typeof asInput.value === 'string') asInput.value = value;
	else el.textContent = value;
}

// UPDATE INVENTORY LINE ITEMS
function updateInventoryLineTotal(input_1: string,input_2: string,input_3: string): void {

	const el1 = document.getElementById(input_1);
	const el2 = document.getElementById(input_2);
	const el3 = document.getElementById(input_3);

	var input1_raw = ftCellValue(el1);
	var input2_raw = ftCellValue(el2);

	var input1 = parseInt(input1_raw);
	var input2 = parseInt(input2_raw);

	if (isNaN(input1) || isNaN(input2)) {
		ftSetCellValue(el3, 'Both inputs must be numbers');
	} else {
		var total = Number(input1_raw) * Number(input2_raw);
		ftSetCellValue(el3, String(total));
	}
}


// UPDATE ALL TOTALS OF LINE ITEMS
function outputSumTotal(input_class: string, output_div: string): void{
	let sum = 0;
	document.querySelectorAll('.' + input_class).forEach(el => {
		sum += +ftCellValue(el);
	});

	sum = Math.round((sum + Number.EPSILON) * 100) / 100;

	const outEl = document.getElementById(output_div);
	if (outEl) outEl.innerHTML = String(sum);
}


/**
 * True when the user is actively viewing a form inside the sidelog drawer.
 *
 * Spec: log drawer must be visible (offsetWidth > 20), the Forms pill must be selected
 * (Bootstrap puts .active on the tab BUTTON, not just the pane), and #div_refresh_sidelog_form
 * must show a loading spinner, an actual form, or other interactive chrome — not the empty
 * placeholder copy ("Forms will go here…") so ambient openItemInfo() still works on that state.
 */
function isSideLogFormPaneActive() {
	// On an FT3D route the legacy pane is permanently inactive (the drawer never opens), but a form
	// can absolutely be on screen — in the HUD's Forms window. Callers use this to decide whether it
	// is safe to open the info panel over a form on a narrow viewport, so answer for the surface the
	// viewer is actually looking at.
	if (ft3dHudSuppressesSideLog()) {
		const ft3dFormsOpen = (window as unknown as { ftFt3dFormsWindowOpen?: () => boolean }).ftFt3dFormsWindowOpen;
		return typeof ft3dFormsOpen === 'function' ? ft3dFormsOpen() : false;
	}
	if (typeof isSideLogOpen === 'function' && !isSideLogOpen()) {
		return false;
	}
	const tabBtn = document.getElementById('pills_sidelog_form_tab');
	const formPillActive = !!(
		tabBtn
		&& (
			tabBtn.classList.contains('active')
			|| tabBtn.getAttribute('aria-selected') === 'true'
		)
	);
	if (!formPillActive) {
		const pane = document.getElementById('pills_sidelog_form');
		const paneActive = !!(
			pane
			&& (pane.classList.contains('active') || pane.classList.contains('show'))
		);
		if (!paneActive) return false;
	}
	const formHost = document.getElementById('div_refresh_sidelog_form');
	if (!formHost) return false;
	// queryData() drops the global window.loader HTML here — guard must stay true during fetch or
	// forceOpenSideInfoPanel / openItemInfo can slip through before the form DOM mounts.
	if (formHost.querySelector('.spinner-border')) {
		return true;
	}
	return formHost.querySelector('form, .inventory-container-form, input, select, textarea, button.btn-success, button.btn-primary') !== null;
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
async function openItemInfoValue(input: string): Promise<void> {

    // GET INPUT VALUE SAFELY
	const inputElement = document.getElementById(input) as HTMLInputElement | HTMLSelectElement | null;
	if (!inputElement) return;
    const data_in_1 = inputElement.value;
    if (!data_in_1) return;

	// FT3D's GM tools and item details share the Workspace window. An automatic
	// dropdown preview would switch that window to info and hide the form before
	// the GM can submit it. Keep the draft visible; explicit info links still use
	// openItemInfo directly. Editor pickers outside this shared host keep previews.
	if (inputElement.closest('#div_refresh_gm_viewer')
		?.closest('[data-ft3d-workspace-mount]')) {
		return;
	}

	// Delegate to openItemInfo() so the side panel goes through the canonical render
	// path. Critically that path calls window.ftMountListDisplays() after injecting the
	// panel HTML, which mounts the list-display TS bundle (the DEFAULT render now). This
	// stale copy used to query open_table_item_inc.php directly and never mounted, so a
	// dropdown selection (e.g. an ancestry user-choice ability) showed only the
	// server-rendered name + flavor while the Range/AOE/status/contest list-display
	// detail stayed in an unrendered mount div. add_search=false: a dropdown pick should
	// not push a navigation-history entry.
	if (typeof openItemInfo === 'function') {
		await openItemInfo(data_in_1, false);
		return;
	}

	// Fallback (openItemInfo unavailable): legacy inline query + an explicit mount so the
	// list-display bundle still renders.
	if (
		typeof isNarrowViewport === 'function'
		&& isNarrowViewport()
		&& isSideLogFormPaneActive()
	) {
		return;
	}

	forceOpenSideInfoPanel();


	const sl_context = getSidelogContext();
	if (!sl_context) return;

	const {
		campaign_id,
		location_id,
		encounter_id,
		caster_id,
		caster_main_id,
	} = sl_context;


    // QUERY ITEM INFO
    await queryData(
        BASE_URL + "options/tables/open_table_item_inc.php",
        'div_refresh_side_info',
        {
            data_in_1,
			campaign_id,
            location_id,
			encounter_id,
			caster_id,
			caster_main_id,
        }
    );
	sideInfoActivateItemTab();
	if (typeof window.ftMountListDisplays === 'function') {
		try { window.ftMountListDisplays(); } catch (e) { console.warn('ftMountListDisplays failed', e); }
	}
	if (typeof window.ftMountRuneIcons === 'function') {
		try { window.ftMountRuneIcons(); } catch (e) { console.warn('ftMountRuneIcons failed', e); }
	}
	if (typeof window.ftMountThumbnails === 'function') {
		try { window.ftMountThumbnails(); } catch (e) { console.warn('ftMountThumbnails failed', e); }
	}
	// Carousel mounts (.ft-carousel-mount) delivered by this side-panel inject — full-doc scan beside the
	// thumbnail re-invoke. This is the primary AJAX path for server-rendered creature/inventory cards.
	if (typeof window.ftMountCarousels === 'function') {
		try { window.ftMountCarousels(); } catch (e) { console.warn('ftMountCarousels failed', e); }
	}
	if (typeof window.ftMountIcons === 'function') {
		try { window.ftMountIcons(); } catch (e) { console.warn('ftMountIcons failed', e); }
	}
	// Hydrate parse-text mounts (.ft-pt-mount) + bare [table:]/[random_table:] mounts (.ft-pt-table-mount)
	// this side-panel inject delivered. Idempotent, mirroring the ftMountIcons re-invoke above.
	if (typeof window.ftMountParsedText === 'function') {
		try { window.ftMountParsedText(); } catch (e) { console.warn('ftMountParsedText failed', e); }
	}
	if (typeof window.ftMountParseTextTables === 'function') {
		try { window.ftMountParseTextTables(); } catch (e) { console.warn('ftMountParseTextTables failed', e); }
	}
	if (typeof window.ftMountInventoryTiles === 'function') {
		try { window.ftMountInventoryTiles(); } catch (e) { console.warn('ftMountInventoryTiles failed', e); }
	}
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
async function showStatusEffects(encounter_creature_id: string): Promise<void> {
    
    forceOpenSideInfoPanel();
    
    // QUERY ITEM INFO
    await queryData(
		BASE_URL + "options/tools/statuses/show_creature_statuses_inc.php",
		'div_refresh_side_info', 
		{ 
			encounter_creature_id,
		}
	);
	sideInfoActivateItemTab();
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////



////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
async function showSummon(encounter_creature_id: string): Promise<void> {
    
    forceOpenSideInfoPanel();
    
    // QUERY ITEM INFO
    await queryData(
		BASE_URL + "options/tools/encounter_creatures/show_summon_inc.php", 
		'div_refresh_side_info', 
		{ 
			encounter_creature_id,
		}
	);
	sideInfoActivateItemTab();
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


function openLinkNewTab(varurl: string): void {
	window.open(varurl, "_blank");
}


// CHECK SPELL SLOTS
// WE CHECK THE SPELL SLOTS AND DISABLE BUTTONS IF NEEDBE
function checkSpellSlots(){

	var class_cantrips_prepared = parseInt(ftSidePanelInputValue('input_hidden_class_cantrips_prepared'));
	var max_class_cantrips_known = parseInt(ftSidePanelInputValue('input_hidden_max_class_cantrips_known'));
	var class_spells_prepared = parseInt(ftSidePanelInputValue('input_hidden_class_spells_prepared'));
	var max_class_spells_known = parseInt(ftSidePanelInputValue('input_hidden_max_class_spells_known'));


	// UPDATE SPAN VALUE
	const spanCantrips = document.getElementById('span_class_cantrips_prepared');
	if (spanCantrips) spanCantrips.innerHTML = String(class_cantrips_prepared);
	const spanSpells = document.getElementById('span_class_spells_prepared');
	if (spanSpells) spanSpells.innerHTML = String(class_spells_prepared);


	// CHECK CANTRIPS -> DISABLE
	const cantripDisabled = class_cantrips_prepared >= max_class_cantrips_known;
	document.querySelectorAll<HTMLInputElement>('.class_prepared_cantrips').forEach(el => { el.disabled = cantripDisabled; });

	// CHECK SPELLS -> DISABLE
	const spellsDisabled = class_spells_prepared >= max_class_spells_known;
	document.querySelectorAll<HTMLInputElement>('.class_prepared_spells').forEach(el => { el.disabled = spellsDisabled; });

}




// REPLACE URL PARAMETERS
/*

    The parameter does not exist in the original url
    The parameter is the only parameter
    The parameter is first or last
    The new parameter value is the same as the old
    The url ends with a ? character
    \b ensures another parameter ending with paramName won't be matched

*/
function replaceUrlParam(url: string, paramName: string, paramValue: string | null)
{
    if (paramValue == null) {
        paramValue = '';
    }
    var pattern = new RegExp('\\b('+paramName+'=).*?(&|#|$)');
    if (url.search(pattern)>=0) {
        return url.replace(pattern,'$1' + paramValue + '$2');
    }
    url = url.replace(/[?#]$/,'');
    return url + (url.indexOf('?')>0 ? '&' : '?') + paramName + '=' + paramValue;
}


/*
document.querySelectorAll('tbody tr').forEach(row => {
    row.addEventListener('click', function () {
        Array.from(this.parentElement.children).forEach(sib => sib.classList.remove('random_table_choice_selected'));
        this.classList.add('random_table_choice_selected');
    });
});
*/


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// OPEN SIDE FORM (CHARACTER SHEET FORMS / ENCOUNTER FORMS / ETC.)
// Opening a form is always an explicit user action (edit button click), so on narrow viewports
// we force the sidelog drawer open rather than only pulsing the tab — otherwise the form loads
// into a hidden panel and the user can't see what they just asked to edit.
function openSideForm(post: string): void {
	
	// OPEN SIDELOG (force on mobile — explicit user intent)
	forceOpenSideLog();
	
	// CLICK SIDELOG FORMS TAB
	document.getElementById('pills_sidelog_form_tab')?.click();
	
	queryData(
		post, 
		'div_refresh_sidelog_form', 
		{ }
	).then(() => {
		// Focus after the asynchronous form and its widgets finish initializing.
		const search = document.querySelector<HTMLInputElement>(
			'#div_refresh_sidelog_form [data-ft-add-inventory-search="1"] input[type="search"]',
		);
		if (search?.isConnected && search.getClientRects().length) {
			search.focus({ preventScroll: true });
		}
	});
	
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// To select item name
// HAS TO BE OUTSIDE READY FOR SOME REASON
function selectSearchItem(val: string): void {

    // HIDE SEARCH BOX
    const suggestionBox = document.getElementById('search_bar_suggestion_box');
    if (suggestionBox) suggestionBox.style.display = 'none';
    
    // SIMPLY GO TO THE SITE
    queryData(
		BASE_URL + "options/search/search_results_inc.php", 
		'div_refresh_search_bar', 
		{ 
			data_in_1: val 
		}
	);
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////



////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
function selectSearchInventoryItem(val: string): void{

	// HIDE SEARCH BOX
	const suggestionBox = document.getElementById('search_bar_inventory_objects_suggestion_box');
	if (suggestionBox) suggestionBox.style.display = 'none';
	// INPUT THE SEARCH BAR VARIABLE
	const searchInput = document.getElementById('input_inventory_objects_search_bar_items') as HTMLInputElement | null;
	if (searchInput) searchInput.value = val;
	
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// checkIfValidUUID — EXTRACTED to includes/js/core-helpers.js (loaded FIRST in the footer, before
// this file). Bare calls below resolve to the window global installed there.
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////





////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// USED FOR array_unique
function onlyUnique<T>(value: T, index: number, self: T[]): boolean {
	return self.indexOf(value) === index;
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
function clearSelection() {
    if(document.selection && document.selection.empty) {
        document.selection.empty();
    } else if(window.getSelection) {
        var sel = window.getSelection();
        sel?.removeAllRanges();
    }
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
function ftEmbeddedCampaignAudioSuppressed(): boolean {
	try {
		const params = new URLSearchParams(window.location.search);
		return params.get('ft3d_campaign_view') === '1' || params.get('ft3d_embed') === '1';
	} catch {
		return false;
	}
}

// my_campaign.php owns the audible site shell. Its FT3D iframe carries the same encounter
// runtime only as a visual/query-only world, so letting that child initialize soundboard audio
// doubles music, ambience, and combat effects against the parent's canonical players.
function ftStopSuppressedEmbeddedAudio(audioPlayer?: HTMLAudioElement | null): boolean {
	if (!ftEmbeddedCampaignAudioSuppressed()) return false;
	if (audioPlayer) {
		try { audioPlayer.pause(); } catch { /* already stopped */ }
	}
	return true;
}

function playAudioOutput(audio_player: HTMLAudioElement, audio_output: string): void {
    if (ftStopSuppressedEmbeddedAudio(audio_player)) return;
    ftDebugLog('audio', { phase: 'playAudioOutput', audio_output });

    // Set the audio source
    audio_player.src = audio_output;

    // Load the audio element
    audio_player.load();

    // Set the volume if needed
    // audio_player.volume  = 0.3;

    // Play the audio when it can be played through
	/*
    audio_player.oncanplaythrough = function() {
        console.log("Audio can play through. Starting playback.");
        audio_player.play();
    };
	*/

    // Fallback to loadeddata event in case canplaythrough doesn't fire
    audio_player.onloadeddata = function() {
        if (audio_player.readyState >= 3) { // HAVE_FUTURE_DATA
            ftDebugLog('audio', { phase: 'loadeddata', readyState: audio_player.readyState });
            audio_player.play();
        }
    };

    // Error handling for better user experience
    audio_player.onerror = function(e: string | Event) {
        console.error("Error playing audio: ", e);
        // Additional error handling logic can go here
    };
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
function playAudioFileFromInput1(input_1: string): void{
	const data_in_1 = ftSidePanelInputValue(input_1);
	playAudioID(data_in_1);
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/** Ambience audio name select: preview + persist encounter when on modify encounter page. */
function onEncounterAmbienceAudioSelected(inputId: string): void {
	openSideLog();
	document.getElementById('pills-soundboard-tab')?.click();
	playAudioFileFromInput1(inputId);
	if (typeof modifyEncounter === 'function') {
		modifyEncounter();
	}
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
function playAudioFile(data_in_1: string): void{
	playAudioID(data_in_1);
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
async function buildTurnWidgetGMControls() {

    const refreshDiv = document.getElementById('div_refresh_turn_widget_gm_controls');
	if (!refreshDiv) return;

	// PHP → JS conversion complete (Phases 1-5, 2026-05-21) + TypeScript carve-out
	// the same day. The PHP partial turn_widget_gm_controls_inc.php is retired to
	// _ai_code_files/retired_php/turn_widget/. All rendering happens in the bundle at
	// includes/js/dist/turn-widget-bundle.js (source: includes/js-src/turn-widget/*.ts)
	// from the cached encounter_arr + campaign_arr.
	const encounterId = getElementValue('encounter_id');
	if (!encounterId) {
		if (refreshDiv.textContent !== 'GM Widget: No encounter loaded. Please load encounter.') {
			replaceHydratedHtml(refreshDiv, 'GM Widget: No encounter loaded. Please load encounter.');
		}
		return;
	}
	// Cache-first (2026-06-13): prefer the in-memory blobs that the WS handlers keep current — the
	// instant `encounter_stats_update` frame patches encounterArr and writes it to encounterCache
	// BEFORE calling us. The old unconditional getEncounterArr()/getCampaignArr() did TWO network
	// round-trips per call, and the encounter one RACED the post-action recompile (it could return the
	// pre-transition encounter), so the GM turn controls lagged seconds behind the instant combat-shape
	// flip on enter/exit combat. encounterCache/campaignCache hold the same full blobs the fetchers
	// return (getCampaignArr even populates campaignCache itself); fall back to the fetch on cold cache.
	const encArr =
		(window.FateTide?.encounterCache instanceof Map && window.FateTide.encounterCache.get(encounterId))
		|| await getEncounterArr(encounterId);
	const campaignId =
		ftEncounterField(encArr, 'encounter_master_parent_id')
		|| getElementValue('campaign_id')
		|| window.FateTide?.campaign?.id
		|| '';
	const camArr = campaignId
		? ((window.FateTide?.campaignCache instanceof Map && window.FateTide.campaignCache.get(campaignId))
			|| await getCampaignArr(campaignId))
		: null;
	// A rebuild that started out of combat can be suspended by the campaign fetch while initiative
	// updates the retained encounter and starts a second rebuild. Never let that older async call
	// resume and overwrite the newer combat controls with its captured pre-initiative snapshot.
	// Re-resolve both route identity and encounter truth immediately before touching the DOM.
	const activeEncounterId = getElementValue('encounter_id');
	if (String(activeEncounterId).toUpperCase() !== String(encounterId).toUpperCase()) return;
	const renderEncArr =
		(window.FateTide?.encounterCache instanceof Map && window.FateTide.encounterCache.get(encounterId))
		|| encArr;

	const renderCamArr = campaignId
		? window.FateTide?.campaignCache?.get(campaignId) || camArr
		: null;
	// Read the live timer after any cold-cache await; an older suspended paint must not rewind it.
	const prevTimerState = window.ftGetTurnTimerState?.();
	const wasTimerRunning = prevTimerState?.running === 1;
	const prevRem = parseInt(String(prevTimerState?.remaining ?? ''), 10);
	const hadValidRemaining = wasTimerRunning && !Number.isNaN(prevRem) && prevRem >= 0;
	const patchControls = (window as unknown as {
		ftPatchTurnWidgetGMControls: (root: HTMLElement, encounter: unknown, campaign: unknown) => boolean;
	}).ftPatchTurnWidgetGMControls;
	const timerInserted = patchControls(refreshDiv, renderEncArr, renderCamArr);
	if (!timerInserted) { refreshAmbiencePlayingIndicator(); return; }
	reinitializeTurnTimerUi();

	const renderedTimerSpan = document.getElementById('span_creature_turn_timer_remaining');
	const renderedRemaining = parseInt(String(renderedTimerSpan?.textContent ?? ''), 10);
	const renderedAutoEndTurn =
		typeof isCookieSet === 'function'
		&& isCookieSet('cookie_auto_end_turn_off')
		&& String(getCookieValue('cookie_auto_end_turn_off')) === '1'
			? 1
			: 0;

	if (wasTimerRunning) {
		window.ftSetTurnTimerState?.({ running: 1 }, 'gm-controls-rebuild');
		const pauseBtn = document.getElementById('btn_pause_turn_timer');
		const resumeBtn = document.getElementById('btn_resume_turn_timer');
		if (pauseBtn) pauseBtn.style.display = '';
		if (resumeBtn) resumeBtn.style.display = 'none';
		if (hadValidRemaining) {
			window.ftSetTurnTimerState?.({ remaining: prevRem, running: 1 }, 'gm-controls-rebuild-preserve');
		}
	} else if (!Number.isNaN(renderedRemaining) && renderedRemaining >= 0) {
		window.ftSetTurnTimerState?.({
			remaining: renderedRemaining,
			running: 0,
			auto_end_turn: renderedAutoEndTurn,
		}, 'gm-controls-render');
	}

	refreshAmbiencePlayingIndicator();
}
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// buildLog
//
// History recovery keeps its authoritative read, but only renders changed saved groups.
// The native renderer owns death/trade paired rows, contests, visibility and ordering
// within a group. The window bridge keeps that renderer in its owning bundle.
type MountedLogWindow = {
	root: HTMLElement;
	context: string;
	list: HTMLElement | null;
	request: number;
	revision: number;
	rows: Map<string, { row: LogWindowRow; revision: number }>;
	rendered: Map<string, { signature: string; visible: boolean }>;
};
let ftMountedLogWindow: MountedLogWindow | null = null;

function ftLogWindowGroupKey(row: LogWindowRow): string {
	return String(row.roll_id || `row:${row.roll_i}`);
}

function ftLogWindowGroups(rows: Iterable<LogWindowRow>): Map<string, LogWindowRow[]> {
	const groups = new Map<string, LogWindowRow[]>();
	for (const row of [...rows].sort((a, b) => Number(b.roll_i) - Number(a.roll_i))) {
		const key = ftLogWindowGroupKey(row);
		if (!groups.has(key)) groups.set(key, []);
		groups.get(key)!.push(row);
	}
	return groups;
}

// Authorized live delivery has already painted and cued this row. Remember references,
// not a second encounter snapshot, so a recovery response cannot erase a newer arrival.
document.addEventListener('ft-roll-authorized', event => {
	const state = ftMountedLogWindow;
	const row = (event as CustomEvent<{ roll?: LogWindowRow }>).detail?.roll;
	if (!state || !row || row.roll_i == null) return;
	state.rows.set(String(row.roll_i), { row, revision: ++state.revision });
	const key = ftLogWindowGroupKey(row);
	const group = [...state.rows.values()].map(value => value.row).filter(value => ftLogWindowGroupKey(value) === key);
	group.sort((a, b) => Number(b.roll_i) - Number(a.roll_i));
	state.rendered.set(key, { signature: JSON.stringify(group), visible: !!state.list && Array.from(state.list.children)
		.some(node => (node as HTMLElement).dataset.ftRollId === key || (node as HTMLElement).dataset.ftLiveGroup === key) });
});

function ftReconcileLogWindow(state: MountedLogWindow, json: LogWindowInput): void {
	const retainedFocus = document.activeElement as HTMLElement | null;
	if (!state.list || state.list.parentElement !== state.root) {
		replaceHydratedHtml(state.root, '<ul class="list-group list-group-flush"></ul>');
		state.list = state.root.firstElementChild as HTMLElement;
		state.rendered.clear();
	}
	const list = state.list;
	const groups = ftLogWindowGroups(json.rolls);
	for (const key of state.rendered.keys()) if (!groups.has(key)) state.rendered.delete(key);
	const mounted = new Map<string, HTMLElement[]>();
	for (const node of Array.from(list.children) as HTMLElement[]) {
		const key = node.dataset.ftLogWindowKey || node.dataset.ftLiveGroup || node.dataset.ftRollId || '';
		if (!mounted.has(key)) mounted.set(key, []);
		mounted.get(key)!.push(node);
	}
	const release = (node: HTMLElement) => {
		window.destroyTomSelectIn?.(node);
		window.disposeBootstrapTooltipsWithin?.(node);
		node.remove();
	};
	for (const [key, nodes] of mounted) {
		if (groups.has(key)) continue;
		for (const node of nodes) release(node);
	}
	let cursor = list.firstElementChild;
	for (const [key, rows] of groups) {
		const signature = JSON.stringify(rows);
		let nodes = mounted.get(key) || [];
		const prior = state.rendered.get(key);
		if (prior?.signature !== signature || (prior.visible && !nodes.length)) {
			const built = window.renderLogWindowFromJson({ ...json, rolls: rows });
			if (!built) continue;
			const template = document.createElement('template');
			template.innerHTML = built.html;
			const fresh = Array.from(template.content.querySelector('ul')?.children || []) as HTMLElement[];
			// All nodes from a native group share this key, including untagged death/trade
			// anchors. Never infer a paired boundary from a neighbouring unrelated row.
			for (const node of fresh) node.dataset.ftLogWindowKey = key;
			const focused = document.activeElement as HTMLElement | null;
			const restoreFocus = focused && nodes.some(node => node.contains(focused));
			for (const node of nodes) {
				for (const open of node.querySelectorAll('.collapse.show[id]')) {
					template.content.getElementById(open.id)?.classList.add('show');
					for (const button of template.content.querySelectorAll('[data-bs-toggle="collapse"]')) {
						if (button.getAttribute('data-bs-target') === `#${open.id}`) button.setAttribute('aria-expanded', 'true');
					}
				}
				for (const control of node.querySelectorAll<HTMLElement>('input[id], select[id], textarea[id], button[id]')) {
					const next = template.content.getElementById(control.id);
					if (next?.tagName === control.tagName) next.replaceWith(control);
				}
			}
			const focusTarget = restoreFocus && focused?.getAttribute('data-bs-target');
			const nextFocus = restoreFocus && focused?.id ? template.content.getElementById(focused.id)
				: focusTarget ? Array.from(template.content.querySelectorAll<HTMLElement>('[data-bs-target]'))
					.find(node => node.getAttribute('data-bs-target') === focusTarget) : null;
			if (cursor && nodes.includes(cursor as HTMLElement)) cursor = nodes[nodes.length - 1]!.nextElementSibling;
			for (const node of nodes) release(node);
			for (const node of fresh) list.insertBefore(node, cursor);
			for (const node of fresh) hydrateSubtree(node);
			window.activateLogCollapseTasks?.(built.collapseLabelTasks || []);
			if (restoreFocus) (focused?.isConnected ? focused : nextFocus)?.focus({ preventScroll: true });
			nodes = fresh;
			state.rendered.set(key, { signature, visible: nodes.length > 0 });
			cursor = nodes.length ? nodes[nodes.length - 1]!.nextElementSibling : cursor;
			continue;
		}
		for (const node of nodes) {
			if (node !== cursor) list.insertBefore(node, cursor);
			cursor = node.nextElementSibling;
		}
	}
	if (retainedFocus?.isConnected && document.activeElement !== retainedFocus) retainedFocus.focus({ preventScroll: true });
}

async function buildLog(context: { campaignId?: string; encounterId?: string; memoOnly?: boolean } = {}) {
	const refreshEl = document.getElementById('div_sidelog_results');

	if (!refreshEl && !context.memoOnly) return;
	const campaign_id  = context.campaignId || ftSidePanelCampaignId();
	const encounter_id = context.encounterId || ftSidePanelEncounterId();
	const contextKey = `${campaign_id.toUpperCase()}:${encounter_id.toUpperCase()}`;
	let state: MountedLogWindow | null = null;
	if (refreshEl && !context.memoOnly) {
		if (ftMountedLogWindow?.root !== refreshEl || ftMountedLogWindow.context !== contextKey) {
			ftMountedLogWindow = { root: refreshEl, context: contextKey, list: null, request: 0, revision: 0,
				rows: new Map(), rendered: new Map() };
		}
		state = ftMountedLogWindow;
	}
	const request = state ? ++state.request : 0;
	const revision = state?.revision || 0;
	const surfaceEntryScopes = {};
	window.dispatchEvent(new CustomEvent('ft:log-rows-requested', { detail: { encounterId: encounter_id,
		campaignId: campaign_id, surfaceEntryScopes } }));

	const resp = await fetch(BASE_URL + 'options/game/log/log_display_inc.php', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ campaign_id, encounter_id, format: 'json' }),
	});
	const json = await resp.json();
	if (!resp.ok || json.ok !== true || !Array.isArray(json.rolls)) throw new Error('log:window-unavailable');
	if (state) {
		if (ftMountedLogWindow !== state || state.request !== request) return;
		const rows: MountedLogWindow['rows'] = new Map();
		for (const row of json.rolls as LogWindowRow[]) rows.set(String(row.roll_i), { row, revision: 0 });
		for (const [key, value] of state.rows) if (value.revision > revision) rows.set(key, value);
		state.rows = rows;
		json.rolls = [...rows.values()].map(value => value.row).sort((a, b) => Number(b.roll_i) - Number(a.roll_i));
	}
	window.dispatchEvent(new CustomEvent('ft:log-rows-received', { detail: { rows: json.rolls, encounterId: encounter_id,
		campaignId: json.campaign_id || campaign_id, logWindow: json.log_window, surfaceEntryScopes } }));
	if (state) ftReconcileLogWindow(state, json);
}
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// clearTriggers
// Soft-deletes every row in mmdb.triggers for the active caster (and current
// encounter if any). Optional explicit `{ caster_id, encounter_id?, campaign_id? }`
// — Core Abilities passes IDs from the sheet so we don't rely on sidelog hidden inputs.
// Without explicit, reads helper-backed FateTide context.

function clearTriggersFromCoreAbilitiesButton(btn: HTMLElement): void {
	if (!btn || !btn.getAttribute) return;
	const ec = String(btn.getAttribute('data-ft-clear-triggers-ec') || '').trim();
	clearTriggers({
		caster_id: ec,
		encounter_id: String(btn.getAttribute('data-ft-clear-triggers-enc') || '').trim(),
		campaign_id: String(btn.getAttribute('data-ft-clear-triggers-camp') || '').trim(),
	});
}

type ClearTriggersContext = {
	caster_id: string;
	encounter_id?: string | null;
	campaign_id?: string | null;
};

async function clearTriggers(explicit: ClearTriggersContext | null = null): Promise<void> {
	const casterId =
		explicit && explicit.caster_id != null && String(explicit.caster_id).trim() !== ''
			? String(explicit.caster_id).trim()
			: String(ftSidePanelInputValue('caster_id')).trim();
	if (!casterId || !checkIfValidUUID(casterId)) {
		display_warning('Select a caster first to clear their triggers.');
		return;
	}

	const ok = window.confirm('Clear all trigger overlays for the active caster?\n\nAll trigger_inactive / per-target overlays will be removed. Default auto-fire returns immediately.');
	if (!ok) return;

	// Client truth only (websocket/status-truth.ts ftCommitTriggerClear — status_truth_commit_inc.php): the shared actor-row loader gets
	// the caster's row (held, its encounter loaded, or the encounter-less row itself); a refusal is shown in words. The legacy
	// clear_triggers_for_caster_inc.php is gone (N6 2026-10-01, R-064).
	const truthEncounter = explicit && explicit.encounter_id ? String(explicit.encounter_id) : String(getElementValue('encounter_id') ?? '');
	if (typeof window.ftCommitTriggerClear !== 'function') { display_warning('Clear Triggers is not available on this page — reload and try again.'); return; }
	await window.ftCommitTriggerClear({ actorId: casterId, encounterId: truthEncounter || undefined });
}

/**
 * Deferred primary click on triggered-sheet ability buttons: the real use snippet waits ~FT_TRIGGER_DOUBLE_CLICK_MS
 * so two quick taps run only badge.toggle (no duplicated useAbility), matching double-tap UX on mobile/desktop.
 *
 * Buttons without data-ft-trigger-use-snippet behave normally (outside this helper).
 *
 * Trade-off: a single deliberate use waits for this window (~one animation frame perceptible delay).
 *
 * @param {MouseEvent} ev
 * @param {HTMLButtonElement} btn
 * @returns {boolean}
 */
function ftTriggeredAbilityDebouncedPrimaryClick(ev: MouseEvent, btn: HTMLButtonElement): boolean {
	const snippetRaw = btn.getAttribute('data-ft-trigger-use-snippet');
	const snippet = typeof snippetRaw === 'string' ? snippetRaw.trim() : '';
	if (!snippet) {
		return true;
	}

	ev.preventDefault();
	ev.stopPropagation();
	if (typeof ev.stopImmediatePropagation === 'function') {
		ev.stopImmediatePropagation();
	}

	const aids = btn.getAttribute('data-ability_id') || snippet;
	const pendingByAbility = window._ftTriggeredAbilityPending ??= {};

	const WINDOW_MS =
		typeof window.FT_TRIGGER_DOUBLE_CLICK_MS === 'number' && Number.isFinite(window.FT_TRIGGER_DOUBLE_CLICK_MS)
			? Math.max(150, Math.min(600, window.FT_TRIGGER_DOUBLE_CLICK_MS))
			: 260;

	const now =
		typeof performance !== 'undefined' && typeof performance.now === 'function'
			? performance.now()
			: Date.now();

	const pending = pendingByAbility[aids];

	if (pending && pending.armForSecond && now - pending.t0 < WINDOW_MS) {
		clearTimeout(pending.timer);
		delete pendingByAbility[aids];
		const badge = btn.closest('.ft-trigger-ability-wrapper')?.querySelector<HTMLElement>('.ft-trigger-badge');
		if (badge) {
			badge.click();
		}
		return false;
	}

	if (pending && pending.timer) {
		clearTimeout(pending.timer);
	}

	pendingByAbility[aids] = {
		t0: now,
		armForSecond: true,
		timer: setTimeout(() => {
			delete pendingByAbility[aids];
			try {
				// Preserve the exact generated trigger marker, but do not execute it until the selected
				// ability's target draft is confirmed in FT3D.
				const run = () => { new Function(snippet)(); };
				const abilityId = btn.getAttribute('data-ability_id') || '';
				const casterId = btn.closest<HTMLElement>('[data-ft-caster-id]')?.getAttribute('data-ft-caster-id') || '';
				if (!window.ftDispatchAbilityVariantClick?.(casterId, abilityId, run)) run();
			} catch (err) {
				console.error('ftTriggeredAbilityDebouncedPrimaryClick', err);
			}
		}, WINDOW_MS),
	};
	return false;
}

async function toggleTriggerActive(ability_id: string, caster_id: string, encounter_id: string, trigger_inactive: unknown): Promise<void> {
	// Client truth only (websocket/status-truth.ts ftCommitTriggerToggle): the loader gets the caster's row; a refusal is shown in words.
	// The legacy toggle_trigger_active_inc.php is gone (N6 2026-10-01, R-064).
	if (typeof window.ftCommitTriggerToggle !== 'function') { display_warning('Toggling a trigger is not available on this page — reload and try again.'); return; }
	await window.ftCommitTriggerToggle({ abilityId: ability_id, actorId: caster_id, encounterId: encounter_id || undefined, inactive: trigger_inactive });
}
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
function matchCampaignAudio(audio_player: string, campaign_audio: string | null | undefined): void{
	// Campaign-pushed audio plays only inside the FT3D system (shared/audio-surface.ts).
	if (!ftCampaignAudioSurface()) return;
	
    // Return early if campaign_audio is blank, null, or empty
    if (
		!campaign_audio 
	){
        // console.log(audio_player + " Campaign audio is blank, null, or empty. Skipping...");
        return;
    }
	
	const audioElement = document.getElementById(audio_player) as HTMLAudioElement | null;
	if (!audioElement) return;
	const audioSource = audioElement.src;
	const filenameWithExtension = audioSource.substring(audioSource.lastIndexOf("/") + 1);
	const filenameWithoutExtension = filenameWithExtension.split(".")[0];	
	
	// IF IT DOESNT MATCH, PLAY THIS AUDIO
	if(
		filenameWithoutExtension !== campaign_audio
		&& campaign_audio !== ""
	){
		
		ftDebugLog('audio', { phase: 'matchCampaignAudio', campaign_audio });
		
		// NEED THIS TO PLAY AUDIO B/C EXTENSIONS CAN BE DIFFERENT
		playAudioID(campaign_audio);
		// No loadPostFunctions() here (2026-08-27): playAudioID only swaps <audio> sources — no injected
		// markup to hydrate. This was the LAST document-wide pass left on a plain FT3D campaign clock tick
		// (handleCampaignUpdate → ftSyncViewerAmbience → matchCampaignAudio). Regression:
		// tools/test/campaign-time-node-identity-staging-smoke.mjs.

	}
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ENCOUNTER-SCOPED AMBIENCE (the authored slots are per-encounter; this makes PLAYBACK per-encounter too).
//
// The three ambience slots (encounter_amb / encounter_amb_night / encounter_amb_weather) are authored ON
// THE ENCOUNTER, but the runtime "what's playing" pointer used to be the campaign-global
// campaigns.campaign_encounter_amb — so the whole campaign could only broadcast ONE ambience at a time and
// a viewer heard the GM's loaded-encounter track regardless of which encounter they were actually in.
//
// ftSyncViewerAmbience() instead resolves ambience as a pure function of the VIEWER'S OWN encounter — for
// every viewer, GM and players alike: each hears the ambience of the active encounter, resolved
// from THAT encounter's slots + its own weather + the campaign clock (sun state). Each slot tries its
// authored track, then the SAME slot of the location type. Audible weather falls through to night/sunset
// when applicable, then day; calm night/sunset falls through to day. The removed campaign-global pointer
// cannot bleed another scene's track into this viewer. GM preview buttons remain local (playAudioFile).
//
// Routed through matchCampaignAudio so an already-playing loop is never restarted, and called from
// campaign_update (re-resolve on a sun-state day/night tick — and so an unrelated campaign tick never
// re-asserts a stale campaign-global track over a viewer's own encounter), the caster encounter-switch
// (re-scope the instant a player moves through an encounter link), and encounter_update weather/env
// changes on the encounter the viewer is in.
// The weather slot is the location's rain/storm bed, so only weather that brings its own sound plays it (owner
// 2026-10-24). Clear, Partly Cloudy, Cloudy and Hazy keep the day/night bed — they used to play a thunderstorm
// over 208 calm encounters, because the importer fills the weather slot from the location-type default.
const FT_WEATHER_AUDIBLE_IDS = [
    '72D05502-08AA-159C-AA98-D6548840F964', // Ashfall
    '1A485B48-C8F4-DF38-BB69-4FE828FDC8A5', // Dust Storm
    '6B3F3EA7-DF1A-7F16-38D2-12542984DBD7', // Hail
    'AFF257FE-DA6E-02E3-86FA-E06FF598180A', // Heavy Rain
    'BE024638-C2F8-8F7B-2C08-BF017BBFB56F', // Heavy Snow
    '5DF3C480-7754-4872-302E-63CC3C4A9F03', // Raining
    'C4097260-89C0-CB3C-19DD-B76A305EC039', // Sleet
    '5ADA1900-D1AD-198D-A299-EB7EC01ADCBD', // Snowing
    '828845FB-4210-2C7A-23CE-4223EDAD52D5', // Thunderstorm
];
const FT_SUN_STATE_NIGHT_IDS = [
    'E4B7F803-C689-A9A5-0FB7-EB8D9946874E', // sun_state_night
    '7BA5009E-B362-AE2F-ABB6-D06D54D306EA', // sun_state_sunset  (ft_classify_campaign_sun_state_day_night → 'night')
];

// Bucket-aware reader mirroring PHP encounter_field()/campaign_field(): read arr[bucket][key] first
// (the source-table bucket since the 2026-06 reshape), else the legacy top-level key; unwrap a catalog
// shell ({items:{item_id}} — e.g. encounter_weather) and a legacy [0] wrap back to the bare value.
function ftSidePanelRecord(value: unknown): Record<string, unknown> | null {
	return value !== null && typeof value === 'object' && !Array.isArray(value)
		? value as Record<string, unknown>
		: null;
}

function ftReadBucketScalar(arr: unknown, bucket: string, key: string): string {
	const root = ftSidePanelRecord(arr);
	if (!root) return '';
	let v: unknown;
	const b = root[bucket];
	if (b && typeof b === 'object' && !Array.isArray(b) && key in b) {
		v = (b as Record<string, unknown>)[key];
	} else {
		v = root[key];
	}
	if (Array.isArray(v)) v = v[0];
	const valueRecord = ftSidePanelRecord(v);
	if (valueRecord) {
		const items = ftSidePanelRecord(valueRecord.items);
		if (items?.item_id != null) v = items.item_id;
    }
    if (Array.isArray(v)) v = v[0];
    return v == null ? '' : String(v).trim();
}

// Availability comes from the held catalog: a deleted id is not a playable authored slot.
// Keep the established weather → night → day fallback after trying each slot's location-type default.
// Calm skies never select the weather bed. No usable candidate means silence (AmbiencePlayback).
type AmbienceAudioCatalog = AudioCatalog & { ambienceDefaults?: { item_id: string; slot: string; audio_id: string }[] };
function ftResolveEncounterAmbId(encounterArr: unknown, sunStateId: string, catalog: AmbienceAudioCatalog | null): string {
    if (!encounterArr || typeof encounterArr !== 'object') return '';
    const locationType = ftReadBucketScalar(encounterArr, 'encounters', 'encounter_location_type').toUpperCase();
    const playable = (id: string): string => catalog?.files?.[id.toUpperCase()]?.startsWith('amb/') ? id : '';
    const slot = (name: string): string => {
        const authored = playable(ftReadBucketScalar(encounterArr, 'encounters', name));
        if (authored) return authored;
        const defaults = Array.isArray(catalog?.ambienceDefaults) ? catalog.ambienceDefaults : [];
        const saved = defaults.find(row => typeof row?.item_id === 'string' && row.item_id.toUpperCase() === locationType
            && row.slot === `default_${name}_id` && typeof row.audio_id === 'string' && playable(row.audio_id));
        return saved ? playable(saved.audio_id) : '';
    };
    const weather = ftReadBucketScalar(encounterArr, 'encounters', 'encounter_weather');
    const night = FT_SUN_STATE_NIGHT_IDS.includes(String(sunStateId).toUpperCase());
    if (FT_WEATHER_AUDIBLE_IDS.includes(weather.toUpperCase())) {
        return slot('encounter_amb_weather') || (night ? slot('encounter_amb_night') : '') || slot('encounter_amb');
    }
    if (night) return slot('encounter_amb_night') || slot('encounter_amb');
    return slot('encounter_amb');
}

// ── WIND OVERLAY (2026-08-18; client-resolved per room 2026-09-10) ─────────────────────────
// Wind is NOT an authored slot: it derives from a WindLevel band and loops on a SECOND player
// (audio_player_ambience_wind, sidelog.php) UNDER the ambience bed — beds switch day/night/weather
// while the wind keeps blowing across the switch. The band is the listener ROOM's (resolveRoomAmbience:
// the room's own band, off, or the encounter's `encounter_wind`, with roofed rooms quiet by default),
// and its file comes from the checked-in WIND_AUDIO_CATALOG — no PHP call (owner 2026-09-10; the old
// resolve_wind_ambience_inc.php lookup is R-015). 'Still' (or an unset/unknown band) STOPS the overlay —
// unlike the bed, silence here is a correct authored state, not a resolution failure.
function ftStopWindOverlay(windEl: HTMLAudioElement): void {
    // playAudioOutput starts playback from a deferred loadeddata handler; drop it so a band that
    // was switched off mid-load cannot start playing afterwards.
    windEl.onloadeddata = null;
    if (!windEl.paused) windEl.pause();
    windEl.removeAttribute('data-current-audio-id');
    windEl.removeAttribute('data-wind-level-id');
    delete windEl.dataset.windAutoplayBlocked;
}

/** play() the current band; remember an autoplay refusal so the next gesture-driven sync retries it. */
function ftStartWindOverlay(windEl: HTMLAudioElement, audioId: string): void {
    void windEl.play().then(() => { delete windEl.dataset.windAutoplayBlocked; }).catch((error: unknown) => {
        if (windEl.getAttribute('data-current-audio-id') === audioId && (error as { name?: string })?.name === 'NotAllowedError') {
            windEl.dataset.windAutoplayBlocked = '1';
        }
    });
}

/** roomWind: 'inherit' = the encounter's band, 'off' = none, otherwise the room's own WindLevel id. */
function ftSyncWindOverlay(encounterArr: unknown, roomWind: string): void {
    try {
        const windEl = document.getElementById('audio_player_ambience_wind') as HTMLAudioElement | null;
        if (!windEl) return;
        const windLevelId = roomWind === 'off' ? ''
            : roomWind === 'inherit' ? ftReadBucketScalar(encounterArr, 'encounters', 'encounter_wind') : roomWind;
        const track = windAudioFor(windLevelId);
        if (!track) { ftStopWindOverlay(windEl); return; }
        const sameBand = windEl.getAttribute('data-current-audio-id') === track.audioId && windEl.getAttribute('src') === track.file;
        if (sameBand) {
            // Looping, or still buffering: never restart it — a restart re-downloads the file (the scene's room
            // re-publish lands ~2 s after the push, mid-buffer).
            if (!windEl.paused || (windEl.readyState < HTMLMediaElement.HAVE_FUTURE_DATA && !windEl.error)) return;
            // Paused and buffered: retry only a start the browser refused (autoplay) — play, don't reload.
            // Otherwise the viewer paused it on the native control; that holds until the band changes.
            if (windEl.dataset.windAutoplayBlocked === '1') ftStartWindOverlay(windEl, track.audioId);
            return;
        }
        windEl.setAttribute('data-current-audio-id', track.audioId);
        windEl.setAttribute('data-wind-level-id', track.windLevelId);
        delete windEl.dataset.windAutoplayBlocked;
        ftDebugLog('audio', { phase: 'ftSyncWindOverlay', windLevel: track.windLevel, audio_id: track.audioId, roomWind });
        playAudioOutput(windEl, track.file);
        // playAudioOutput only starts from loadeddata when readyState >= 3 by then; when loadeddata lands at 2
        // the loop sat silent until some later sync re-ran (measured ~2 s on staging). Start it on canplay,
        // unless a stop or a newer band has taken the element meanwhile.
        windEl.addEventListener('canplay', () => {
            if (windEl.paused && windEl.getAttribute('data-current-audio-id') === track.audioId) ftStartWindOverlay(windEl, track.audioId);
        }, { once: true });
    } catch (e) {
        // The wind overlay must never break the ambience sync path that calls it.
        try { ftDebugLog('audio', { phase: 'ftSyncWindOverlay', error: String(e) }); } catch (e2) { /* ignore */ }
    }
}

const ftRoomAmbiencePlayback = new AmbiencePlayback();
let ftRoomAmbience: { encounterId: string; roomId: string | null; listener: string;
    ambience: { mode: string; audioId: string; wind: string }; source: MessageEventSource | null } | null = null;
let ftAmbienceSyncVersion = 0;

window.addEventListener('message', event => {
    if (event.origin !== window.location.origin || event.data?.type !== 'ft3d-room-ambience') return;
    const frame = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
    if (event.source !== window && (!frame || event.source !== frame.contentWindow)) return;
    if (event.source === window && !document.querySelector('[data-ft-room-ambience]')) return;
    const payload = event.data.payload;
    // Kept even when it names an encounter the page has not adopted yet (the frame can post before
    // caster-update repoints the viewer): the scene only re-posts on change, so dropping it would lose the
    // room until the listener moved. ftSyncViewerAmbience applies it only while the encounters match.
    if (!payload || typeof payload.encounterId !== 'string'
        || !/^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i.test(payload.encounterId)) return;
    const settings = payload.ambience;
    if (!settings || !['inherit', 'custom', 'silence'].includes(settings.mode)
        || !(['inherit', 'off'].includes(settings.wind)
            || (typeof settings.wind === 'string' && /^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i.test(settings.wind)))
        || typeof settings.audioId !== 'string'
        || (settings.audioId !== '' && !/^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i.test(settings.audioId))) return;
    ftRoomAmbience = { ...payload, source: event.source };
    void ftSyncViewerAmbience();
});
// The first attempt may precede the browser's audio unlock.
window.addEventListener('pointerdown', () => { void ftSyncViewerAmbience(); }, { passive: true });
window.addEventListener('keydown', () => { void ftSyncViewerAmbience(); }, { passive: true });
window.addEventListener('load', () => { void ftSyncViewerAmbience(); });
window.addEventListener('ft:scene-interactive', () => { void ftSyncViewerAmbience(); });
window.addEventListener('message', event => {
    if (event.origin !== window.location.origin || event.data?.type !== 'ft3d-scene-loading'
        || event.data?.payload?.loading !== false) return;
    const frame = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
    if (frame && event.source === frame.contentWindow) void ftSyncViewerAmbience();
});

// Push-driven re-resolution (wind S1, 2026-09-10). A GM wind / weather / track change, and a room-setting
// save, reach every viewer as encounter_update(_delta) → handleEncounterUpdate → 'ft:encounter-state-received'
// after the cache is current. Re-run only when an input this resolver reads changed on the viewer's own
// encounter — before this, a GM wind change reached players on their next click. The shape is a JSON string
// compared by value (an unchanged blob usually keeps the very same string, which compares instantly).
let ftAmbienceInputsKey = '';
let ftAmbienceInputsShape: unknown = null;
window.addEventListener('ft:encounter-state-received', event => {
    const detail = (event as CustomEvent<{ encounterId?: string; encounterArr?: unknown }>).detail;
    const encounterId = String(detail?.encounterId ?? '').trim().toUpperCase();
    if (!encounterId || encounterId !== ftSidePanelEncounterId().trim().toUpperCase()) return;
    const arr = detail?.encounterArr;
    const key = [encounterId, ...['encounter_wind', 'encounter_weather', 'encounter_amb', 'encounter_amb_night', 'encounter_amb_weather', 'encounter_location_type']
        .map(field => ftReadBucketScalar(arr, 'encounters', field))].join('|');
    const bucket = ftSidePanelRecord(ftSidePanelRecord(arr)?.encounters);
    const shape = bucket && 'encounter_shape' in bucket ? bucket.encounter_shape : ftSidePanelRecord(arr)?.encounter_shape;
    if (key === ftAmbienceInputsKey && shape === ftAmbienceInputsShape) return;
    ftAmbienceInputsKey = key;
    ftAmbienceInputsShape = shape;
    void ftSyncViewerAmbience();
});
// The compact stats frame carries weather at mutation time, ahead of the canonical update.
window.addEventListener('ft:encounter-lighting-changed', event => {
    const detail = (event as CustomEvent<{ encounterId?: string; encounterWeatherId?: string | null }>).detail;
    if (!detail || !('encounterWeatherId' in detail)) return;
    if (String(detail.encounterId ?? '').trim().toUpperCase() !== ftSidePanelEncounterId().trim().toUpperCase()) return;
    void ftSyncViewerAmbience();
});

// Parsed room boxes of the viewer's encounter_shape, keyed by the raw string: the sync runs on every click
// and keypress, and a merged encounter's shape can carry hundreds of boxes plus authoring drafts.
type FtAmbienceShapeBox = { id?: string; creation?: { function?: string; ambience?: unknown } };
let ftAmbienceShapeCache: { raw: string; boxes: FtAmbienceShapeBox[] } | null = null;
function ftAmbienceShapeBoxes(raw: string): FtAmbienceShapeBox[] {
    if (ftAmbienceShapeCache?.raw === raw) return ftAmbienceShapeCache.boxes;
    const parsed = JSON.parse(raw) as { boxes?: FtAmbienceShapeBox[] };
    const boxes = Array.isArray(parsed?.boxes) ? parsed.boxes : [];
    ftAmbienceShapeCache = { raw, boxes };
    return boxes;
}

function ftStopWindOverlayIfPresent(): void {
    const windEl = document.getElementById('audio_player_ambience_wind') as HTMLAudioElement | null;
    if (windEl) ftStopWindOverlay(windEl);
}

async function ftSyncViewerAmbience(campaignArrIn?: unknown): Promise<void> {
    const syncVersion = ++ftAmbienceSyncVersion;
    try {
        if (ftEmbeddedCampaignAudioSuppressed()) return;
        // Campaign ambience plays only inside the FT3D system (shared/audio-surface.ts). Return WITHOUT stopping
        // anything: a bed on a non-FT3D page can only be the viewer's own soundboard preview.
        if (!ftCampaignAudioSurface()) {
            ftDebugLog('audio', { phase: 'ftSyncViewerAmbience', skip: 'not-ft3d-surface' });
            return;
        }
        // Room/campaign updates can precede scene construction. Neither the bed nor wind may
        // start a media request until the current canvas and the page are interactive.
        if (!ambienceSceneInteractive()) return;
        const ft = window.FateTide;
        const campaignId = ft && ft.campaign ? ft.campaign.id : null;
        const campaignArr =
            campaignArrIn
            || (campaignId && ft && ft.campaignCache && ft.campaignCache.get ? ft.campaignCache.get(campaignId) : null)
            || (ft ? ft.campaignArr : null);

        // campaign sun state (global clock) drives the day vs night slot.
        const sunState = ftReadBucketScalar(campaignArr, 'campaigns', 'campaign_sun_state');

        // Ambience is scoped by ENCOUNTER: every viewer — GM and players alike — hears the ambience of the
        // encounter they're currently in/viewing, resolved from THAT encounter's own slots (+ its weather +
        // the campaign clock). ftGetActiveEncounterId() is the viewer's active encounter (repointed to a player's
        // caster encounter by caster-update.ts; the GM's viewed/loaded encounter for the GM). There is NO campaign-
        // global fallback anymore — the campaign_encounter_amb pointer was removed; if we can't resolve the
        // viewer's encounter we leave the current BED running rather than bleeding in another scene's track.
        // Wind stops instead: a band with no resolved room/encounter behind it is not a correct state.
        const viewerEncounterId = ftSidePanelEncounterId().trim();
        if (!viewerEncounterId || typeof checkIfValidUUID !== 'function' || !checkIfValidUUID(viewerEncounterId)) {
            ftDebugLog('audio', { phase: 'ftSyncViewerAmbience', skip: 'no-viewer-encounter' });
            ftStopWindOverlayIfPresent();
            return;
        }

        let encounterArr = ft && ft.encounterCache && ft.encounterCache.get ? ft.encounterCache.get(viewerEncounterId) : null;
        if (!encounterArr && typeof getEncounterArr === 'function') {
            try { encounterArr = await getEncounterArr(viewerEncounterId); } catch (e) { /* non-fatal */ }
        }
        if (!encounterArr) {
            ftDebugLog('audio', { phase: 'ftSyncViewerAmbience', skip: 'encounter-unresolved', viewerEncounterId });
            ftStopWindOverlayIfPresent();
            return;
        }

        // Play the encounter's scoped track. A blank id stops the bed (AmbiencePlayback), so an encounter with
        // no usable authored/type-default bed in the fallback chain — or a silent room — plays no bed.
        if (syncVersion !== ftAmbienceSyncVersion || viewerEncounterId !== ftSidePanelEncounterId().trim()) return;
        const activeFrame = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
        // A message from a replaced frame is dead; one for another encounter is kept for when it becomes current.
        if (ftRoomAmbience && ftRoomAmbience.source !== window && ftRoomAmbience.source !== activeFrame?.contentWindow) ftRoomAmbience = null;
        const roomMessage = ftRoomAmbience && ftRoomAmbience.encounterId.toUpperCase() === viewerEncounterId.toUpperCase()
            ? ftRoomAmbience : null;
        let settings = roomMessage?.ambience;
        // The parent receives authoritative encounter updates while its retained scene iframe may
        // still have an older room draft. Resolve saved audio metadata from that current blob.
        if (roomMessage && roomMessage.source !== window && roomMessage.roomId) {
            const raw = ftReadBucketScalar(encounterArr, 'encounters', 'encounter_shape');
            try {
                const room = ftAmbienceShapeBoxes(raw).find(box => box.id === roomMessage.roomId);
                settings = resolveRoomAmbience(room?.creation?.ambience, room?.creation?.function, roomIsRoofed(room));
            } catch { /* a legacy encounter without a shape retains the scene's resolved settings */ }
        }
        // An FT3D scene on this page reports the listener's room once it is ready. Until it has, the room is
        // unknown: hold the wind off rather than blowing the encounter's wind into a roofed room while the
        // scene loads (the bed still follows the encounter).
        const scenePending = !roomMessage && (!!activeFrame || !!document.querySelector('[data-ft-room-ambience]'));
        let scopedAmb = '';
        if (settings?.mode === 'custom' && settings.audioId) scopedAmb = settings.audioId;
        else if (settings?.mode !== 'silence') {
            const catalog = await loadAudioCatalog(); // memoized page catalog, never re-fetch the encounter on a clock delta
            scopedAmb = ftResolveEncounterAmbId(encounterArr, sunState, catalog);
        }
        if (syncVersion !== ftAmbienceSyncVersion || viewerEncounterId !== ftSidePanelEncounterId().trim()) return;
        ftDebugLog('audio', { phase: 'ftSyncViewerAmbience', viewerEncounterId, scopedAmb, sunState, scenePending });
        if (roomMessage) {
            const audio = document.getElementById('audio_player_ambience_1');
            if (audio) {
                audio.dataset.ambienceRoom = roomMessage.roomId ?? '';
                audio.dataset.ambienceListener = roomMessage.listener;
                audio.dataset.ambienceMode = settings?.mode ?? 'inherit';
            }
        }
        // The bed's file comes from the versioned audio catalog in the browser (R-016 / wind handoff S4) — no
        // server lookup. '' = "track unavailable": AmbiencePlayback resets and the next sync retries.
        void ftRoomAmbiencePlayback.sync(scopedAmb, async id => {
            const file = await resolveAudioFile(id);
            return file?.type === 'amb' ? file.url : '';
        });
        // Wind rides the same resolution triggers (encounter switch, weather/env/wind push,
        // sun tick) but plays on its own overlay player, independent of the bed above.
        ftSyncWindOverlay(encounterArr, settings?.mode === 'silence' || scenePending ? 'off' : settings?.wind ?? 'inherit');
    } catch (e) {
        // Ambience scoping must never break the WS handlers that call it.
        try { ftDebugLog('audio', { phase: 'ftSyncViewerAmbience', error: String(e) }); } catch (e2) { /* ignore */ }
    }
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
async function playAudioID(audio_ref: string): Promise<void> {
    if (ftEmbeddedCampaignAudioSuppressed()) return;
    try {
        // Resolved in the browser from the versioned audio catalog (shared/audio-catalog.ts resolveAudioFile):
        // an audio id, or '<type>:<url>' from the admin audio list. The retired play_audio_file_inc.php round
        // trip did this lookup on the server (R-016).
        const file = await resolveAudioFile(audio_ref);
        if (!file) {
            ftDebugLog('audio', { phase: 'playAudioID', audio_ref, skipReason: 'not-in-catalog' });
            return;
        }
        const playerId = file.type === 'amb' ? 'audio_player_ambience_1'
            : file.type === 'mx' ? 'audio_player_music_1' : 'audio_player_effect_1';
        const audio = document.getElementById(playerId) as HTMLAudioElement | null;
        if (!audio) return;
        playAudioOutput(audio, file.url);
    } catch (error) {
        console.error("Error in playAudioID:", error);
    }
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
async function playCampaignAudioID(audio_id: string): Promise<void> {
    if (ftEmbeddedCampaignAudioSuppressed()) return;
    const campaign_id = ftSidePanelCampaignId();

    if (!audio_id || !campaign_id || !checkIfValidUUID(audio_id) || !checkIfValidUUID(campaign_id)) {
        console.error("Invalid audio_id or campaign_id");
        return;
    }

    try {
        const response = await fetch(BASE_URL + "options/game/audio/play_campaign_audio_file_inc.php", {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ audio_id, campaign_id })
        });

        // === SAFETY: Check HTTP status first ===
        if (!response.ok) {
            console.error(`HTTP error! status: ${response.status}`);
            return;
        }

        let result = await response.text();
        result = result.trim(); // ← CRITICAL: removes any stray whitespace/newlines

        // === DEBUG: If parse fails, show exactly what we got ===
        let audio_arr;
        try {
            audio_arr = JSON.parse(result);
        } catch (error) {
            console.error("❌ Error parsing audio JSON response");
            console.error("Raw response was:", result.substring(0, 500));
            return;
        }

        // === Handle error returned from PHP ===
        if (audio_arr.error) {
            console.error("Server error:", audio_arr.error);
            return;
        }

        // === Validate data ===
        if (!audio_arr.audio_type || !audio_arr.audio_file) {
            console.error("Invalid audio data received:", audio_arr);
            return;
        }

        const audio_type = audio_arr.audio_type;
        const audio_file = audio_arr.audio_file;

        // GET AUDIO PLAYERS (with null checks)
        const audio_player_effect_1 = document.getElementById('audio_player_effect_1') as HTMLAudioElement | null;
        const audio_player_effect_2 = document.getElementById('audio_player_effect_2') as HTMLAudioElement | null;
        const audio_player_ambience_1 = document.getElementById('audio_player_ambience_1') as HTMLAudioElement | null;
        const audio_player_music_1 = document.getElementById('audio_player_music_1') as HTMLAudioElement | null;

        if (!audio_player_effect_1 || !audio_player_effect_2 || !audio_player_ambience_1 || !audio_player_music_1) {
            console.error("Audio players are not properly defined in the DOM");
            return;
        }

        // SELECT CORRECT PLAYER
        let audio;
        if (audio_type === "sfx") {
            audio = (audio_player_effect_1.duration > 0 && !audio_player_effect_1.paused)
                ? audio_player_effect_2
                : audio_player_effect_1;
        } else if (audio_type === "amb") {
            audio = audio_player_ambience_1;
        } else if (audio_type === "mx") {
            audio = audio_player_music_1;
        } else {
            console.error("Unknown audio type:", audio_type);
            return;
        }

        // PLAY (skip silence)
        if (audio_type !== "silence") {
            if (audio_type === "amb") {
                audio.setAttribute('data-current-audio-id', audio_id);
            }
            playAudioOutput(audio, audio_file);
            if (audio_type === "amb") {
                refreshAmbiencePlayingIndicator();
            }
        }

    } catch (error) {
        console.error(`Error in playCampaignAudioID (audio_id: ${audio_id}, campaign_id: ${campaign_id}):`, error);
    }
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// refreshAmbiencePlayingIndicator
// Mirrors the live state of audio_player_ambience_1 onto every
// [data-ambience-button="1"] button by toggling its inner
// [data-ambience-playing-badge="1"] span. Lazy-attaches play/pause/ended
// listeners on the audio element on first call so the GM's manual use of
// the sidelog mixer's native controls also drives the badge.
function refreshAmbiencePlayingIndicator() {
    var audio = document.getElementById('audio_player_ambience_1') as HTMLAudioElement | null;
    if (!audio) return;

    if (!audio.dataset.ftAmbBadgeListenersAttached) {
        audio.addEventListener('play', refreshAmbiencePlayingIndicator);
        audio.addEventListener('pause', refreshAmbiencePlayingIndicator);
        audio.addEventListener('ended', refreshAmbiencePlayingIndicator);
        audio.dataset.ftAmbBadgeListenersAttached = '1';
    }

    var currentId = audio.getAttribute('data-current-audio-id') || '';
    var isPlaying = !audio.paused && !audio.ended;

    var buttons = document.querySelectorAll('[data-ambience-button="1"]');
    for (var i = 0; i < buttons.length; i++) {
        var btn = buttons[i];
		if (!btn) continue;
        var badge = btn.querySelector<HTMLElement>('[data-ambience-playing-badge="1"]');
        if (!badge) continue;
        var match = currentId && btn.getAttribute('data-audio-id') === currentId && isPlaying;
        badge.style.display = match ? 'inline-block' : 'none';
    }
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Backward-compatible shim. Ambience is now scoped per-encounter (ftSyncViewerAmbience); the old
// campaign-global campaign_encounter_amb pointer + its play_campaign_encounter_amb_inc.php endpoint were
// removed. The GM load/push handlers + the weather-toggle handler still call this by name, so it stays —
// it just re-resolves the viewer's own encounter ambience now instead of fetching the campaign track.
async function playCampaignEncounterAmb() {
    try { await ftSyncViewerAmbience(); } catch (e) { /* non-fatal */ }
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


// L7b (2026-09-27): prepareSpellList / resetPreparedSpellList (and their commit helper) are retired — the PC sheet's Prepare
// Spells tab commits list switches / resets itself (janus-engine/spells/spell-prep-commit.ts); nothing called the globals (R-059).


///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
async function gmLoadCampaignEncounter(encounter_loaded: string): Promise<void> {

    // SPINNER
    openSideLog();


	const sl_context = getSidelogContext();
	if (!sl_context) return;

	const {
		campaign_id,
		encounter_id,
		roll_private,
	} = sl_context;

	// Validate required fields
	const rules = {
		campaign_id: { type: 'uuid', value: campaign_id },
		// encounter_id here is only the CURRENTLY-loaded encounter, sent as an audio
		// crossfade "from" hint — the server derives the real source per-PC/target and
		// treats this as optional. Hard-requiring it wrongly blocks a valid push/load
		// when nothing is loaded yet (e.g. first push from a campaign map). Validate the
		// shape only when present.
		encounter_id: { type: 'uuid', value: encounter_id, optional: true },
	};
	const validationResult = validateContext(rules, 'gmLoadCampaignEncounter');
	if (!validationResult.valid) {
        console.error(validationResult.error);
        return;
    }


	const div_refresh_gm_viewer = document.getElementById('div_refresh_gm_viewer');


	await queryData(
		`${BASE_URL}options/tools/encounters/gm_load_encounter/load_campaign_encounter_inc.php`,
		'div_sidelog_warning',
		{
			campaign_id,
			encounter_id, // GET CURRENT ENCOUNTER INFO TO TRANSITION AUDIO
			encounter_loaded,
			roll_private,
		}
	);

	if (div_refresh_gm_viewer) div_refresh_gm_viewer.innerHTML = '';


	// change encounter_id
	const activeEncounterId = ftSidePanelSetEncounterId(encounter_loaded, 'gm-load-campaign-encounter');

	await syncTimeControlsForEncounterId(activeEncounterId);
	
	await playCampaignEncounterAmb();
	document.getElementById('pills_combat_log_tab')?.click(); // click the sidelog pill
	clearLegacySelection(); // encounter transition teardown

}
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
async function gmRelocateCampaignCreatures(
	relocation_route: string,
	encounter_loaded: string,
): Promise<Record<string, unknown> | null> {
	openSideLog();
	const context = getSidelogContext();
	if (!context) return null;
	const { campaign_id, encounter_id, roll_private } = context;
	const validation = validateContext({
		campaign_id: { type: 'uuid', value: campaign_id },
		encounter_id: { type: 'uuid', value: encounter_id, optional: true },
		encounter_loaded: { type: 'uuid', value: encounter_loaded },
	}, 'gmRelocateCampaignCreatures');
	if (!validation.valid) { console.error(validation.error); return null; }
	const relocate = (window as Window & {
		ftGeometryRelocation?: (data: Record<string, unknown>) => Promise<Record<string, unknown>>;
	}).ftGeometryRelocation;
	if (!relocate) { displayWarning('Transfer unavailable: browser relocation is not loaded.'); return null; }
	try {
		// The legacy query admission suppresses both handled and refused commands with null.
		// Await the native receipt so a refused save cannot navigate to an unsaved campaign pointer.
		const result = await relocate({ campaign_id, encounter_id, encounter_loaded, roll_private, relocation_route });
		if (result.ok !== true) return null;
		const viewer = document.getElementById('div_refresh_gm_viewer');
		if (viewer) viewer.innerHTML = '';
		return result;
	} catch (error) {
		displayWarning(error instanceof Error ? error.message : String(error));
		return null;
	}
}

async function gmPushPartyToEncounter(
	encounter_loaded: string,
	destination_encounter_id: string = encounter_loaded,
): Promise<void> {
	const result = await gmRelocateCampaignCreatures('gm_push_party_to_encounter_inc.php', encounter_loaded);
	if (!result) return;
	// Named arrival points carry an inventory UUID; the receipt carries its saved encounter UUID.
	const activeEncounterId = ftSidePanelSetEncounterId(String(result.destinationEncounterId ?? destination_encounter_id), 'gm-push-party-to-encounter');
	await syncTimeControlsForEncounterId(activeEncounterId);
	await playCampaignEncounterAmb();
	document.getElementById('pills_combat_log_tab')?.click();
	clearLegacySelection(); // encounter transition teardown
}

async function gmPushAllToEncounter(encounter_loaded: string): Promise<void> {
	const result = await gmRelocateCampaignCreatures('gm_push_all_to_encounter_inc.php', encounter_loaded);
	if (!result) return;
	const activeEncounterId = ftSidePanelSetEncounterId(String(result.destinationEncounterId ?? encounter_loaded), 'gm-push-all-to-encounter');
	await syncTimeControlsForEncounterId(activeEncounterId);
	await playCampaignEncounterAmb();
	document.getElementById('pills_combat_log_tab')?.click();
	clearLegacySelection(); // encounter transition teardown
}

async function gmPullAllCreaturesFromEncounter(encounter_loaded: string): Promise<void> {
	if (!await gmRelocateCampaignCreatures('gm_pull_all_creatures_from_encounter_inc.php', encounter_loaded)) return;
	await playCampaignEncounterAmb();
	document.getElementById('pills_combat_log_tab')?.click();
	clearLegacySelection(); // encounter transition teardown
}
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * The campaign frame (ft3d_scene_host.php?ft3d_campaign_view=1) is read-only for play, but a GM
 * who has entered the shell's Edit Scene mode edits the encounter IN PLACE — that mode is the
 * replacement for the full-page form. The body class is set only by the frame's edit-mode
 * receiver, which itself honours the toggle only when the server bootstrap says the viewer can
 * manage the campaign; both are UX gates, modify_encounter_inc.php re-validates permission.
 */
function ft3dCampaignEditModeWritable(): boolean {
	if (!document.body.classList.contains('ft3d-campaign-edit-mode-active')) return false;
	try {
		const raw = document.getElementById('modify_encounter_creation_engine_bootstrap')?.textContent ?? '';
		const bootstrap = raw ? JSON.parse(raw) as { canManageCampaign?: unknown; source?: string; sceneReviewFixture?: unknown; surface?: { canEditScene?: boolean; queryOnly?: boolean } } : null;
		return bootstrap?.canManageCampaign === true && !bootstrap.sceneReviewFixture
			&& bootstrap.source !== 'query-review-fixture' && !bootstrap.surface?.queryOnly
			&& (!bootstrap.surface || bootstrap.surface.canEditScene === true);
	} catch {
		return false;
	}
}

/**
 * The encounter settings form only persists after the GM has actually touched it (2026-09-26). Its controls
 * fire change events while they INITIALISE — Tom Select boots, the location pipeline loads defaults, the
 * max-coordinate inputs blur — and each of those called modifyEncounter(), re-posting the whole form on page
 * load: opening Memory's Rest in the scene host blanked encounter_ceiling (the input is empty on shaped
 * encounters) and rewrote the links, with nobody editing anything. A trusted pointer/key/input event inside
 * the settings form (or a Tom Select dropdown it owns) arms saving for the rest of the page's life.
 */
let ftEncounterSettingsArmed = false;
const ftEncounterSettingsPending = new Set<Promise<unknown>>();
let ftEncounterSettingsQueue: Promise<unknown> = Promise.resolve();
let ftEncounterSettingsError: unknown = null;
Object.assign(window, { ftFlushEncounterSettingsSaves: async () => {
	while (ftEncounterSettingsPending.size) await Promise.allSettled([...ftEncounterSettingsPending]);
	if (ftEncounterSettingsError) throw ftEncounterSettingsError;
} });
if (typeof document !== 'undefined') {
	const armEncounterSettings = (event: Event): void => {
		if (!event.isTrusted || ftEncounterSettingsArmed) return;
		const target = event.target as Element | null;
		if (target?.closest?.('#div_encounter, #modify_encounter_scene_settings, .ts-dropdown, [data-ft-action="modify-encounter"]')) {
			ftEncounterSettingsArmed = true;
		}
	};
	for (const type of ['pointerdown', 'keydown', 'input'] as const) document.addEventListener(type, armEncounterSettings, true);
}

async function modifyEncounter() {
	if (document.body.classList.contains('ft3d-surface-leaving-edit')) return;
	if (!ftEncounterSettingsArmed) {
		ftDebugLog('encounter', { phase: 'modify-suppressed', reason: 'settings-form-not-touched' });
		return;
	}
	// Closed Scene Builder owner-review lane. The review URL must remain safe to open in
	// an ordinary browser: legacy controls can emit change events while their async
	// options initialise, but a query-only review must never persist those values.
	const reviewQuery = new URLSearchParams(window.location.search);
	const campaignViewReadOnly = (reviewQuery.get('ft3d_campaign_view') === '1' || reviewQuery.get('ft3d_embed') === '1')
		&& !ft3dCampaignEditModeWritable();
	if (ft3dFlagParam(reviewQuery, 'halo_review') === '1' || campaignViewReadOnly) {
		ftDebugLog('encounter', {
			phase: 'modify-suppressed',
			reason: campaignViewReadOnly
				? 'ft3d-campaign-view-read-only'
				: 'xz3d-authoritative-halo-query-only',
		});
		return;
	}

    // VARIABLES
    // DEFAULT VALUES IN SIDELOG
    var adventure_id = ftSidePanelInputValueOrNull('input_adventure_id');
    var campaign_id = ftSidePanelCampaignId() || null;    
    var encounter_id = ftSidePanelEncounterId() || null;        

    // IF CAMPAIGN ID IS BLANK, PULL FROM hidden
    if (campaign_id === '' || campaign_id === null) {
        campaign_id = ftSidePanelInputValueOrNull('input_campaign_id');
    }    
    
    // IF ENCOUNTER ID IS BLANK, PULL FROM hidden
    if (encounter_id === '' || encounter_id === null) {
        encounter_id = ftSidePanelInputValueOrNull('hidden_input_modify_encounter_id');
    }
    
    var encounter_name = ftSidePanelInputValue('encounter_name');
    var encounter_master_parent_id = ftSidePanelInputValue('encounter_master_parent_id');
	var encounter_zone_number = ftSidePanelInputValue('encounter_zone_number');
	var encounter_inside = getCheckboxValue('encounter_inside');
    var encounter_location_id = ftSidePanelInputValue('encounter_location_id');
    var encounter_location_picture = ftSidePanelInputValue('encounter_location_picture');
    var encounter_environment = ftSidePanelInputValue('encounter_environment');
    var encounter_weather = ftSidePanelInputValue('encounter_weather');
    // Weather axes (2026-08-14, WEATHER_AXES_PLAN.md): temperature/wind band UUIDs + integer
    // wind direction (degrees 0-359, blows-TOWARD). '' clears to NULL server-side.
    var encounter_tempurature = ftSidePanelInputValue('encounter_tempurature');
    var encounter_wind = ftSidePanelInputValue('encounter_wind');
    var encounter_wind_direction = ftSidePanelInputValue('encounter_wind_direction');
    var encounter_light = ftSidePanelInputValue('encounter_light');
    // Scenery / Backdrop variant (visual only; blank = env default) — ft3d_scene_host.php.
    var encounter_backdrop_variant = ftSidePanelInputValue('encounter_backdrop_variant');
    // Interior/building type (indoor only; blank = derive from env) — ft3d_scene_host.php,
    // tools/xz-parallax/INDOOR_RENDER_PLAN.md §3.
    var encounter_interior_type = ftSidePanelInputValue('encounter_interior_type');
    // Backdrop composition seed (GM "re-roll look"; blank = derive from the encounter id) —
    // ft3d_scene_host.php, PROCEDURAL_BACKDROP_PLAN.md §2.
    var encounter_backdrop_seed = ftSidePanelInputValue('encounter_backdrop_seed');
	
	var encounter_max_x = ftSidePanelInputValue('encounter_max_x');
	var encounter_max_y = ftSidePanelInputValue('encounter_max_y');
	var encounter_min_z = ftSidePanelInputValue('encounter_min_z');
	var encounter_ceiling = ftSidePanelInputValue('encounter_ceiling');
	var encounter_max_z = ftSidePanelInputValue('encounter_max_z');
	
    var encounter_description = ftSidePanelInputValue('encounter_description');
    var encounter_gm_notes = ftSidePanelInputValue('encounter_gm_notes');
    var encounter_mx = ftSidePanelInputValue('encounter_mx_id');
    // Music THEME (audio_category bucket) — distinct from encounter_mx, which is a single
    // audio_id. Blank means "no music of its own"; the server maps '' to NULL.
    var encounter_mx_bucket = ftSidePanelInputValue('encounter_mx_bucket');
    var encounter_mx_combat_bucket = ftSidePanelInputValue('encounter_mx_combat_bucket');
    var encounter_amb = ftSidePanelInputValue('encounter_amb_id');
    var encounter_amb_night = ftSidePanelInputValue('encounter_amb_night_id');
    var encounter_amb_weather = ftSidePanelInputValue('encounter_amb_weather_id');
    var encounter_private = getCheckboxValue('encounter_private');
	
	// Get all selected options from the multiple select (omit if encounter links UI not on page)
	const encounterLinksEl = document.getElementById('encounter_links') as HTMLSelectElement | null;
    var encounter_links = encounterLinksEl
		? Array.from(encounterLinksEl.selectedOptions).map(option => option.value)
		: [];

	openSideLog();


	const write = async () => { await queryData(
		`${BASE_URL}options/tools/encounters/modify_encounter_inc.php`, 
		'div_sidelog_warning', 
		{ 
			adventure_id,
			campaign_id,
			encounter_id,
			encounter_name,
			encounter_master_parent_id,
			encounter_location_id,				
			encounter_zone_number,
			encounter_inside,
			encounter_location_picture,
			encounter_environment,
			encounter_weather,
			encounter_tempurature,
			encounter_wind,
			encounter_wind_direction,
			encounter_light,
			encounter_backdrop_variant,
			encounter_interior_type,
			encounter_backdrop_seed,
			encounter_max_x,
			encounter_max_y,
			encounter_min_z,
			encounter_ceiling,
			encounter_max_z,			
			encounter_description,
			encounter_gm_notes,
			encounter_mx,
			encounter_mx_bucket,
			encounter_mx_combat_bucket,
			encounter_amb,
			encounter_amb_night,
			encounter_amb_weather,
			encounter_private,
			encounter_links, 
		}
	);

		if (new URLSearchParams(location.search).get('ft3d_surface') === 'encounter'
			&& !document.querySelector('#div_sidelog_warning .bg-success')) throw new Error('The encounter settings save was not confirmed.');
		// The row is already saved: a failed re-read only leaves the view stale, so it warns instead of holding the GM in Edit.
		if (document.querySelector('#div_sidelog_warning .bg-success')) await refreshSavedEditorEncounter(String(encounter_id ?? ''))
			.catch(error => displayWarning(error instanceof Error ? error.message : String(error)));
		if (typeof window.afterModifyEncounter === 'function') await window.afterModifyEncounter();
		ftEncounterSettingsError = null;
	};
	const preview = new URLSearchParams(location.search).get('ft3d_surface') === 'encounter';
	const save = preview ? ftEncounterSettingsQueue.then(write) : write();
	if (preview) ftEncounterSettingsQueue = save.catch(() => undefined);
	ftEncounterSettingsPending.add(save);
	try {
		await save;
	} catch (error) {
		ftEncounterSettingsError = error;
		displayWarning(error instanceof Error ? error.message : String(error));
	} finally { ftEncounterSettingsPending.delete(save); }

}
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
let spawnQueue = Promise.resolve();
async function addEncounterCreaturesToEncounter(itemId: string, name = '', look: Record<string, unknown> = {}): Promise<void> {
    const status = document.getElementById('div_add_encounter_creatures_status');
    const encounterId = ftSidePanelEncounterId() || ftSidePanelInputValue('hidden_input_modify_encounter_id') || ftSidePanelLocationId();
    if (!encounterId) { spawnFailure(new Error('spawn:destination-required')); return; }
    const template = ftSidePanelInputValue('input_monster_template_id_text').trim() || ftSidePanelInputValue('monster_template_select').trim();
    if (template && !checkIfValidUUID(template)) { spawnFailure(new Error('spawn:monster-template-identity')); return; }
    const request = { itemId, qty: Number(ftSidePanelInputValue('encounter_creature_qty') || 1),
        vitality: (ftSidePanelInputValue('creature_v_method') || 'average') as 'average' | 'max' | 'random',
        monsterTemplateId: template || null,
        hidden: (document.getElementById('encounter_creature_hidden') as HTMLInputElement | null)?.checked === true,
        private: (document.getElementById('encounter_creature_private') as HTMLInputElement | null)?.checked ? 1 : undefined };
    const send = async (point?: { x: number; y: number; z: number }): Promise<void> => {
        try {
            const encounter = await loadSpawnEncounter(encounterId);
            const ids = await commitCreatureSpawn(encounterId, encounter, { ...request, point });
            if (status) status.textContent = `Added ${ids.length} creature${ids.length === 1 ? '' : 's'}.`;
            await window.afterModifyEncounter?.();
        } catch (error) { if (status) status.textContent = String(error instanceof Error ? error.message : error); spawnFailure(error); }
    };
    const place = (point?: { x: number; y: number; z: number }): Promise<void> => {
        const next = spawnQueue.then(() => send(point)); spawnQueue = next.catch(() => undefined); return next;
    };
    type Start = (request: { encounterId: string; creatureId: string; name: string; pictureUrl: string; widthFt: number; heightFt: number;
        place(point: { x: number; y: number; z: number }): void }) => boolean;
    let host: Window & { ftStartCreaturePlacement?: Start } = window;
    try { if (!host.ftStartCreaturePlacement && window.parent !== window) host = window.parent; } catch { /* cross-origin host */ }
    const width = SIZE_WIDTHS_FT[String(look.sizeId ?? '').toUpperCase()] ?? 5;
    if (host.ftStartCreaturePlacement?.({ encounterId, creatureId: itemId, name, pictureUrl: String(look.pictureUrl ?? ''),
        widthFt: width, heightFt: width, place: point => { void place(point); } })) {
        if (status) status.textContent = 'Click the scene to place the creature.';
        return;
    }
    await place();
}

async function addEncounterLairToEncounter(): Promise<void> {
    const itemId = ftSidePanelInputValue('encounter_lair_template');
    const encounterId = ftSidePanelEncounterId() || ftSidePanelInputValue('hidden_input_modify_encounter_id') || ftSidePanelLocationId();
    try {
        if (!itemId || !encounterId) throw new Error('spawn:lair-selection-required');
        await commitCreatureSpawn(encounterId, await loadSpawnEncounter(encounterId), { itemId, point: { x: 0, y: 0, z: 0 }, loot: false });
        await buildEncounterLairsReport();
    } catch (error) { spawnFailure(error); }
}

async function buildEncounterLairsReport() {
	const reportDiv = document.getElementById('div_refresh_encounter_lairs_report');
	if (!reportDiv) {
		return;
	}

	let campaign_id = ftSidePanelCampaignId() || null;
	let location_id = ftSidePanelLocationId() || null;
	let encounter_id = ftSidePanelEncounterId() || null;

	if (!campaign_id) {
		campaign_id = ftSidePanelInputValueOrNull('input_campaign_id');
	}
	if (!location_id) {
		location_id = ftSidePanelInputValueOrNull('hidden_input_modify_location_id');
	}
	if (!encounter_id) {
		encounter_id = ftSidePanelInputValueOrNull('hidden_input_modify_encounter_id');
	}

	const destination = encounter_id || location_id;
	if (destination) {
		try { await syncSpawnLairs(destination, await loadSpawnEncounter(destination)); }
		catch (error) { spawnFailure(error); }
	}

	await queryData(
		`${BASE_URL}options/tools/encounters/reports/encounter_lairs_report_edit_inc.php`,
		'div_refresh_encounter_lairs_report',
		{
			campaign_id,
			location_id,
			encounter_id,
		}
	);
}
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


// =============================================================================
// Cross-file consumer exports
// Top-level `function foo() {}` declarations in non-module scripts auto-attach to
// `window`, but the explicit assignments below remove ambiguity and match the
// established pattern from prior extractions (tom-select-init.js, trade-trainer-init.js,
// filter-search-init.js, campaign-init.js).
// =============================================================================

Object.assign(window, {
	forceOpenSideInfoPanel,
	openSideInfoPanel,
	closeSideInfoPanel,
	collapseSideInfoPanelKeepContent,
	clearSideInfoPanelContent,
	clearSideInfoExchangePanelContent,
	clearSideInfoMerchantPanelContent,
	clearSideInfoTrainerPanelContent,
	sideInfoHideExchangeTabUi,
	sideInfoShowPartnerTabs,
	sideInfoShowExchangeTabUi,
	sideInfoSetActiveSidePane,
	sideInfoActivateItemTab,
	sideInfoActivateMerchantTab,
	sideInfoActivateTrainerTab,
	sideInfoActivateExchangeTab,
	sideInfoOnMerchantTabClick,
	sideInfoOnTrainerTabClick,
	openSideLog,
	closeSideLog,
	toggleSideLog,
	forceOpenSideLog,
	isSideLogOpen,
	isSideInfoPanelOpen,
	isSideLogFormPaneActive,
	syncMobileLogTabSidelogWarningHighlight,
	ftBootstrapTooltipHideSync,
	ftDisposeBootstrapTooltipInstanceSafe,
	ftClearTransientFloatingUi,
	disposeBootstrapTooltipsWithin,
	_ftFtIconMapClickableAncestorWrapper,
	_ftDisposeBootstrapTooltipOnElement,
	openItemInfoValue,
	showStatusEffects,
	showSummon,
	openLinkNewTab,
	openSideForm,
	checkSpellSlots,
	updateInventoryLineTotal,
	outputSumTotal,
	replaceUrlParam,
	selectSearchItem,
	selectSearchInventoryItem,
	onlyUnique,
	clearSelection,
	playAudioOutput,
	playAudioFileFromInput1,
	onEncounterAmbienceAudioSelected,
	playAudioFile,
	playAudioID,
	playCampaignAudioID,
	playCampaignEncounterAmb,
	matchCampaignAudio,
	ftSyncViewerAmbience,
	refreshAmbiencePlayingIndicator,
	gmLoadCampaignEncounter,
	gmPushPartyToEncounter,
	gmPushAllToEncounter,
	gmPullAllCreaturesFromEncounter,
	buildTurnWidgetGMControls,
	buildLog,
	modifyEncounter,
	addEncounterCreaturesToEncounter,
	addEncounterLairToEncounter,
	buildEncounterLairsReport,
	clearTriggers,
	clearTriggersFromCoreAbilitiesButton,
	ftTriggeredAbilityDebouncedPrimaryClick,
	toggleTriggerActive,
});


export {};
